import { CommonModule } from '@angular/common';
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ContentChild,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SharedModule } from 'src/app/shared/shared.module';
import { CustomErrorTooltipComponent } from '../../custom-error-tooltip/custom-error-tooltip.component';
import { SvgIconComponent } from '../../svg-icon/svg-icon.component';
import { MatTableDataSource } from '@angular/material/table';
import {
  FormConfig,
  IDropdownResponse,
} from 'src/app/shared/models/dynamic.model';
import { ITableProp } from 'src/app/shared/models/tables.model';
import { SelectionModel } from '@angular/cdk/collections';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { startWith, map, Observable, combineLatest } from 'rxjs';
import { ErrorHandlerService } from 'src/app/core/services/error-handler.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { ValidatorService } from 'src/app/shared/services/form-control-validators.service';
import {
  ActionIcons,
  formFieldIds,
  selectionTableNames,
  MasterLookup,
  ModeIcons,
  pageType,
  validationErrorMessages,
  selectedIdForSummaryTable,
  StatusCodes,
  conditionCheckConstants,
  apiCrudActions,
  popupWidth,
  ModalPopupMessages,
  hyperlinkLabels,
  eventDetailsPropertyKeyDisplayProps,
  inValidDecimalInputs,
  miscellaneousCheck,
  apChargeCodeFields,
  SingleDeleteHeaderText,
  popupButtons,
  actionText,
} from 'src/app/shared/utility/constants';
import {
  TableControls,
  AutocompleteControls,
  ApiControls,
  InputControls,
  measurementOptions,
  PostalCodeKeys,
  PalletsKeys,
  MarkUpKey,
  ChargeAmountKey,
  IdentifierKey,
  ChargeAmountKeys,
  DatePickerKeys,
} from '../tables.config';
import { TableService } from '../tables.service';
import { CommonApiService } from 'src/app/shared/services/common-api.service';
import { setRangeInputRowData } from 'src/app/features/components/number-ranges/number-ranges-config';
import { MessagePopupComponent } from '../../message-popup/message-popup.component';
import { ModalService } from 'src/app/shared/services/modal.service';
import { CustomHelpInfoTooltipComponent } from '../../custom-help-info-tooltip/custom-help-info-tooltip.component';
import { CommonPopupService } from 'src/app/shared/services/common-popup.service';
import { MatSelectChange } from '@angular/material/select';
import { FieldClearSuffixComponent } from '../../field-clear-suffix/field-clear-suffix.component';
import { ApChargesService } from '../../ap-charges/ap-charges.service';
import { ArChargesService } from '../../ar-charges/ar-charges.service';
import { SnakbarService } from 'src/app/shared/services/snakbar.service';
import { isActiveElementInsideCdkOverlay } from '../../../directives/keystroke/paginator-keyboard-guards';

@Component({
  selector: 'app-add-inline-edit-table',
  standalone: true,
  imports: [
    CommonModule,
    SvgIconComponent,
    SharedModule,
    CustomErrorTooltipComponent,
    CustomHelpInfoTooltipComponent,
    FieldClearSuffixComponent,
  ],
  templateUrl: './add-inline-edit-table.component.html',
  styleUrl: './add-inline-edit-table.component.scss',
})
export class AddInlineEditTableComponent
  implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy, OnChanges
{
  private readonly destroyRef = inject(DestroyRef);
  @Input() inlineCrudTableCustomizeConfig!: ITableProp[];
  @Input() isFrom!: string;
  @Input() formConfig!: FormConfig;
  @Input() pageMode!: string;
  @Input() isEditIconClicked!: boolean;
  @Input() isLoadingData = false;
  @Input() showInlineEmptyStateMessage = false;
  @Input() inlineEmptyStateMessage = '';
  @Input() parentFormValues?: {
    incomingChargeCurrency?: string;
    conversionRate?: string;
    incomingChargeCurrencyId?: number | null;
    numberRangeTypeName?: string;
  };
  @Input() agentChargeFormValues?: {
    agentInfo?: any;
  };
  @Input() agentChargeCodeDropdownList: IDropdownResponse[] = [];
  @ContentChild('[slot="empty-state"]', { static: false })
  emptyStateContent!: ElementRef;
  @Output() inlineCrudTableFormValue = new EventEmitter<FormArray>();
  @Output() inlineTableSelectionChange = new EventEmitter<any[]>();
  @Output() inlineLocationHasValue = new EventEmitter<any[]>();
  @Output() departmentDropdownsLoaded = new EventEmitter<void>();
  @Output() invoiceIdentifierValue = new EventEmitter<any>();
  @Output() datePickerChange = new EventEmitter<{
    rowIndex: number;
    key: string;
  }>();
  @Output() shipmentIdentifierValueClick = new EventEmitter<any>();
  @Output() invoiceNumberClick = new EventEmitter<any>();
  @Output() agentChargeCodeCrudAction = new EventEmitter<{
    action: string;
    id?: number;
    rowIndex: number;
    data?: any;
  }>();
  @Output() updatedTransitPointValue = new EventEmitter<any>();
  @Output() appendChargeRowFromKeyboard = new EventEmitter<{
    rowIndex: number;
  }>();

  @ViewChild('firstInput', { static: false }) firstInput!: ElementRef;

  // Generic Custom Action Template - allows parent to provide custom action buttons
  @ContentChild('customActionTemplate', { static: false })
  customActionTemplate!: TemplateRef<any>;

  public inlineCrudTableDataSource!: MatTableDataSource<any>;
  public inlineCrudTableForm!: FormGroup;
  public inlineTableDisplayedColumns!: string[];
  public inlineTableSelection = new SelectionModel<any>(true, []);
  public inlineTableSelectAllChecked = false;
  public customiseInlineCrudTableColumns!: ITableProp[];
  public setInlineCrudTableWidth: any = {};
  public setInlineCrudTableErrorTooltip: any = {};
  public setInlineCrudTableTooltipEvent: any = {};
  public actionIcons = ActionIcons;
  public formFields = formFieldIds;
  public selectionTableNames = selectionTableNames;
  public selectedRowModuleId = '';
  public deliveryAgentData!: IDropdownResponse[];
  public filteredDeliveryAgentDataOptions: Observable<IDropdownResponse[]>[] =
    [];
  public rateScaleData!: IDropdownResponse[];
  public filteredRateScaleOptions: Observable<IDropdownResponse[]>[] = [];
  public rateScaleDataBasedOnAgents!: IDropdownResponse[];
  public filteredRateScaleOptionsBasedOnAgents: Observable<
    IDropdownResponse[]
  >[] = [];
  public locationData!: IDropdownResponse[];
  public filteredLocationOptions: Observable<IDropdownResponse[]>[] = [];
  public departmentData!: IDropdownResponse[];
  public filteredDepartmentOptions: Observable<IDropdownResponse[]>[] = [];
  public groupData!: IDropdownResponse[];
  public filteredGroupOptions: Observable<IDropdownResponse[]>[] = [];
  public rolesData!: IDropdownResponse[];
  public filteredRolesOptions: Observable<IDropdownResponse[]>[] = [];
  public selectedGroupData: any[] = [];
  public selectedDepartmentsData: any[] = [];
  public selectedRolesData: any[] = [];
  public selectedLocationsData: any[] = [];
  public combinationGridData: any;
  public inlineCrudTableFormValueChange = false;
  public numberRangesEditSetRange = pageType.numberRangesEditSetRange;
  public rsAccessorialManualEntry = pageType.rateScaleAccessorialManualEntry;
  public isOpened: boolean[] = [];
  public measurementOptionsList = measurementOptions;
  public countryList!: IDropdownResponse[];
  public ibcChargeCodeData!: IDropdownResponse[];
  public filteredIbcChargeCodeOptions: Observable<IDropdownResponse[]>[] = [];
  public agentChargeCodeData!: IDropdownResponse[];
  public filteredAgentChargeCodeOptions: Observable<IDropdownResponse[]>[] = [];
  public chargeCodeData!: IDropdownResponse[];
  public filteredChargeCodeOptions: Observable<IDropdownResponse[]>[] = [];
  public agentChargeTypeData!: IDropdownResponse[];
  public filteredAgentChargeTypeOptions: Observable<IDropdownResponse[]>[] = [];
  public apChargeTypeData!: IDropdownResponse[];
  public filteredApChargeTypeOptions: Observable<IDropdownResponse[]>[] = [];
  public selectedIbcChargeCodeData: any[] = [];
  public selectedAgentChargeCodeData: any[] = [];
  public selectedChargeCodeData: any[] = [];
  public selectedAgentChargeTypeData: any[] = [];
  public selectedApChargeTypeData: any[] = [];
  public shipmentIdentifierData!: IDropdownResponse[];
  public currencyData!: IDropdownResponse[];
  public filteredCurrencyOptions: Observable<IDropdownResponse[]>[] = [];
  public selectedCurrencyData: any[] = [];
  public originalAgentChargeCodeData: any[] = [];
  public agentData!: IDropdownResponse[];

  // Generic/External Data Inputs
  @Input() externalDropdownData: { [key: string]: IDropdownResponse[] } = {};
  @Input() externalAutocompleteKeys: string[] = [];
  @Input() externalApiControlNames: { [key: string]: string } = {};
  /** Overrides the isFrom default column order (SPEC-APL-001 AC-APL-001). */
  @Input() displayedColumnsOverride: string[] | null = null;

  // Generic Filtered Options Storage
  public genericFilteredOptions: {
    [key: string]: Observable<IDropdownResponse[]>[];
  } = {};

  // External typeahead streams keyed by the live row control, so rebuilt rows never keep a stale stream.
  private externalTypeaheadOptions = new WeakMap<
    AbstractControl,
    { data: IDropdownResponse[]; options: Observable<IDropdownResponse[]> }
  >();
  // Last AP invoice validated per row, so the validate API is not re-called for the same pick.
  private validatedApInvoices = new WeakMap<AbstractControl, string>();

  private errorMessages: any;
  private hasFocused = false;
  private departmentsGridData: any;
  private readonly dimensionalWeightFactorTooltip =
    ModalPopupMessages.dimensionalWeightFactorTooltip;
  private readonly mawbDimensionalWeightTooltip =
    ModalPopupMessages.dimensionalMawbWeightTooltip;
  public showLinehaulMismatchBanner = false;
  public lineHaulInfoMessage = ModalPopupMessages.lineHaulInfoMessage;
  public readonly lockedApChargeTooltip =
    ModalPopupMessages.lockedApChargeTooltip;
  public readonly ufoShipmentIdentifierTooltip =
    ModalPopupMessages.ufoShipmentIdentifierTooltip;
  public showLinehaulBannerAfterSave = false;
  private selectedAgentIdForAgentChargeCode: any;
  public editingRowIndex: number | null = null;
  private chargeTableKeydownListener: ((event: Event) => void) | null = null;

  constructor(
    public tableService: TableService,
    public commonService: CommonService,
    private readonly formUtilsService: ValidatorService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly fb: FormBuilder,
    private readonly commonApiService: CommonApiService,
    private readonly commonPopupService: CommonPopupService,
    private readonly modalService: ModalService,
    private readonly apChargesService: ApChargesService,
    private readonly arChargesService: ArChargesService,
    private readonly elementRef: ElementRef,
    private readonly snakbarService: SnakbarService
  ) {}

  ngOnInit(): void {
    if (this.isFrom === selectionTableNames.customerService) {
      this.getMasterLookupType(MasterLookup.deliveryAgent);
      this.getMasterLookupType(MasterLookup.rateScale);
    } else if (this.isFrom === selectionTableNames.UserDepartment) {
      this.getDepartmentAndRoles();
    } else if (this.isFrom === selectionTableNames.DeliveryZone) {
      this.getMasterLookupType(MasterLookup.countries);
    } else if (this.isFrom === selectionTableNames.ApChargeCode) {
      this.getMasterLookupType(MasterLookup.ibcChargeCode);
      this.getMasterLookupType(MasterLookup.agentChargeCode);
      this.getMasterLookupType(MasterLookup.agentChargeTypes);
    } else if (this.isFrom === selectionTableNames.apChargesForApModule) {
      this.getMasterLookupType(MasterLookup.agentChargeCode);
      this.getMasterLookupType(MasterLookup.agentChargeTypes);
      this.getMasterLookupType(MasterLookup.currency);
      this.getMasterLookups(MasterLookup.shipmentIdentifier);
    } else if (this.isFrom === selectionTableNames.arChargesForARModule) {
      this.getMasterLookupType(MasterLookup.ibcChargeCode);
      this.getMasterLookups(MasterLookup.shipmentIdentifier);
    } else if (
      this.isFrom === selectionTableNames.apChargesForShipmentAndMawb
    ) {
      this.getMasterLookupType(MasterLookup.agentInfo);
      this.getMasterLookupType(MasterLookup.currency);
      this.getMasterLookupType(MasterLookup.agentChargeCode);
    } else if (
      this.isFrom === selectionTableNames.arChargesForShipmentAndMawb
    ) {
      this.getMasterLookupType(MasterLookup.ibcChargeCode);
    }

    this.createFormArray();
    this.setRequiredInitialization();
    this.getTableData();

    // Initialize generic dropdowns AFTER form is created
    if (this.externalDropdownData) {
      this.initializeExternalDropdownsForAllRows();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      !this.isLoadingData &&
      changes['parentFormValues'] &&
      !changes['parentFormValues'].firstChange
    ) {
      const prevValues = changes['parentFormValues'].previousValue;
      const currValues = changes['parentFormValues'].currentValue;
      if (
        this.isFrom === selectionTableNames.apChargesForApModule &&
        (prevValues?.conversionRate !== currValues?.conversionRate ||
          prevValues?.incomingChargeCurrency !==
            currValues?.incomingChargeCurrency)
      ) {
        const rows = this.getRows();
        for (let i = 0; i < rows.length; i++) {
          this.calculateChargeAmount(i);
        }
      }
      if (
        this.isFrom === pageType.numberRangesEditSetRange &&
        prevValues?.numberRangeTypeName !== currValues?.numberRangeTypeName
      ) {
        this.applyNumberRangeTypeChange(currValues);
      }
    }
    if (
      changes['agentChargeFormValues'] &&
      (this.isFrom === selectionTableNames.ApChargeCode ||
        this.isFrom === selectionTableNames.apChargesForApModule)
    ) {
      this.handleAgentChargeChanges(changes);
    }
  }

  public initializeExternalDropdownsForAllRows(): void {
    if (!this.externalDropdownData) return;
    Object.keys(this.externalDropdownData).forEach(key => {
      const rows = this.getRows();
      for (let i = 0; i < rows.length; i++) {
        this.autocompleteValueChanges(i, key, this.externalDropdownData[key]);
      }
    });
  }

  private handleAgentChargeChanges(changes: SimpleChanges): void {
    const newAgentId =
      changes['agentChargeFormValues'].currentValue?.agentInfo?.agentId;

    // Skip if agentId hasn't actually changed (prevents unnecessary re-processing)
    const agentIdChanged =
      newAgentId !== this.selectedAgentIdForAgentChargeCode;
    if (agentIdChanged) {
      this.selectedAgentIdForAgentChargeCode = newAgentId;
      if (this.originalAgentChargeCodeData?.length > 0) {
        this.filterAgentChargeDropdown();
      }
    }

    if (changes['agentChargeCodeDropdownList']) {
      const nextList = changes['agentChargeCodeDropdownList'].currentValue;
      // Avoid wiping master-lookup options when parent passes an empty list
      // (e.g. filteredAgentChargeCodeList before CRUD refresh).
      if (Array.isArray(nextList) && nextList.length > 0) {
        this.originalAgentChargeCodeData = nextList;
        this.filterAgentChargeDropdown();
      }
    }
  }

  private applyNumberRangeTypeChange(currValues: any): void {
    const rows = this.getRows();
    const isArInvoice =
      currValues?.numberRangeTypeName === actionText.arInvoice;

    for (let i = 0; i < rows.length; i++) {
      const startNumberControl = rows.at(i).get('startNumber');
      if (!startNumberControl) {
        continue;
      }
      if (isArInvoice && !startNumberControl.value) {
        startNumberControl.setValue('600001');
      }
      this.updateStartNumberValidator(startNumberControl, i);
      startNumberControl.updateValueAndValidity({ emitEvent: false });
    }
  }

  ngAfterViewInit(): void {
    if (!this.isApOrArChargeContext()) {
      return;
    }
    this.chargeTableKeydownListener = (event: Event) => {
      this.handleChargeTableKeydown(event);
    };
    this.elementRef.nativeElement.addEventListener(
      'keydown',
      this.chargeTableKeydownListener,
      true
    );
  }

  ngAfterViewChecked(): void {
    if (
      !this.hasFocused &&
      this.firstInput &&
      this.pageMode == ModeIcons.view
    ) {
      this.firstInput.nativeElement.focus();
      this.hasFocused = true;
      this.cdRef.detectChanges();
    }
  }

  ngOnDestroy(): void {
    if (this.chargeTableKeydownListener) {
      this.elementRef.nativeElement.removeEventListener(
        'keydown',
        this.chargeTableKeydownListener,
        true
      );
      this.chargeTableKeydownListener = null;
    }
    this.cleanupInputRestrictionSubscriptions();
  }

  public getPalletsTooltip(key: string) {
    if (key === 'dimensionalFactor') {
      return this.dimensionalWeightFactorTooltip;
    } else if (key === 'dimensionalWeight') {
      return this.mawbDimensionalWeightTooltip;
    }
    return '';
  }

  private isApChargeSelectableRow(row: any): boolean {
    if (row?.isInputRow) {
      return false;
    }
    return row?.isRowDisable !== false;
  }

  private getSelectableNonInputRows(): any[] {
    if (this.isFrom === selectionTableNames.apChargesForApModule) {
      return this.inlineCrudTableDataSource.data.filter((row: any) =>
        this.isApChargeSelectableRow(row)
      );
    }
    return this.inlineCrudTableDataSource.data.filter(
      (row: any) => row.isRowDisable && !this.isLockedRow(row)
    );
  }

  /** Shipment AP rows already in a published AR invoice (SPEC-APL-001 AC-APL-002). */
  public isLockedRow(row: any): boolean {
    return row?.isLocked === true;
  }

  private isMawbPalletInlineTable(): boolean {
    return (
      this.isFrom === pageType.mawbPallet ||
      this.isFrom === selectionTableNames.mawbPallet
    );
  }

  private getMawbPalletSelectionKey(row: any): string | number | undefined {
    const id = Number(row?.id);
    if (Number.isFinite(id) && id > 0) {
      return id;
    }
    const tempId = row?.tempId;
    if (tempId != null && tempId !== '') {
      return tempId;
    }
    return undefined;
  }

  private inlineSelectionRowsMatch(selected: any, row: any): boolean {
    if (this.isMawbPalletInlineTable()) {
      const selectedKey = this.getMawbPalletSelectionKey(selected);
      const rowKey = this.getMawbPalletSelectionKey(row);
      return (
        selectedKey !== undefined &&
        rowKey !== undefined &&
        selectedKey === rowKey
      );
    }
    const key = this.selectedRowModuleId || selectedIdForSummaryTable.Id;
    return selected[key] == row[key];
  }

  public isNoAddedRowsInAP() {
    return (
      this.isFrom === selectionTableNames.apChargesForApModule &&
      this.getSelectableNonInputRows().length === 0
    );
  }

  public shouldShowInlineEmptyStateMessage(): boolean {
    return this.showInlineEmptyStateMessage && this.pageMode !== ModeIcons.view;
  }

  /** Selects all rows if they are not all selected; otherwise clear inlineTableSelection. */
  public toggleAllRows() {
    if (this.isAllSelected()) {
      this.inlineTableSelection.clear();
      this.inlineTableSelectAllChecked = false;
    } else {
      const nonInputRows = this.getSelectableNonInputRows();
      this.inlineTableSelection.select(...nonInputRows);
      this.inlineTableSelectAllChecked = true;
    }
    this.emitCreateSelectionChange();
  }

  /** Whether the number of selected elements matches the total number of rows. */
  public isAllSelected() {
    const nonInputRows = this.getSelectableNonInputRows();
    return (
      this.inlineTableSelection.selected.length === nonInputRows.length &&
      nonInputRows.length > 0
    );
  }

  public isHeaderCheckboxChecked(): boolean {
    return this.getSelectableNonInputRows().length > 0 && this.isAllSelected();
  }

  public isHeaderCheckboxIndeterminate(): boolean {
    const nonInputRows = this.getSelectableNonInputRows();
    return (
      nonInputRows.length > 0 &&
      this.inlineTableSelection.hasValue() &&
      !this.isAllSelected()
    );
  }

  private pruneStaleInlineSelection(): void {
    const selectableRows = this.getSelectableNonInputRows();
    if (selectableRows.length === 0) {
      if (this.inlineTableSelection.hasValue()) {
        this.inlineTableSelection.clear();
        this.inlineTableSelectAllChecked = false;
        this.emitCreateSelectionChange();
      }
      return;
    }

    const nextSelected: any[] = [];
    for (const selected of this.inlineTableSelection.selected) {
      const currentRow = selectableRows.find(row =>
        this.inlineSelectionRowsMatch(selected, row)
      );
      if (
        currentRow &&
        !nextSelected.some(r => this.inlineSelectionRowsMatch(r, currentRow))
      ) {
        nextSelected.push(currentRow);
      }
    }

    const priorSelected = this.inlineTableSelection.selected;
    const selectionChanged =
      nextSelected.length !== priorSelected.length ||
      nextSelected.some(
        row =>
          !priorSelected.some(
            sel => this.inlineSelectionRowsMatch(sel, row) && sel === row
          )
      );

    if (!selectionChanged) {
      return;
    }

    this.inlineTableSelection.clear();
    if (nextSelected.length > 0) {
      this.inlineTableSelection.select(...nextSelected);
    }
    this.inlineTableSelectAllChecked =
      nextSelected.length > 0 && nextSelected.length === selectableRows.length;
    this.emitCreateSelectionChange();
  }

  // for table body checkbox inlineTableSelection
  public checked(row: any) {
    this.inlineTableSelection.select(row);
    if (!this.isMawbPalletInlineTable()) {
      this.checkForCreateModeId(row);
    }
    const found = this.inlineTableSelection.selected.find(x =>
      this.inlineSelectionRowsMatch(x, row)
    );
    if (found) {
      found.checked = true;
    }
  }

  public unchecked(row: any) {
    if (!this.isMawbPalletInlineTable()) {
      this.checkForCreateModeId(row);
    }
    const found = this.inlineTableSelection.selected.find(x =>
      this.inlineSelectionRowsMatch(x, row)
    );
    if (found) {
      found.checked = false;
      this.inlineTableSelection.deselect(found);
    }
  }

  public isChecked(row: any): boolean {
    return this.inlineTableSelection.selected.some(el =>
      this.inlineSelectionRowsMatch(el, row)
    );
  }

  public getRows() {
    return this.inlineCrudTableForm.get('rows') as FormArray;
  }

  public getTableData() {
    const focusToRestore = this.captureFocusedChargeField();
    this.inlineCrudTableDataSource = new MatTableDataSource(
      this.getRows().getRawValue()
    );
    this.pruneStaleInlineSelection();
    if (focusToRestore) {
      this.restoreFocusedChargeField(focusToRestore);
    }
  }

  /**
   * AP/AR charge grids: a new data source re-renders every row, destroying the focused input
   * (e.g. async invoice-number lookup finishing after Tab moved to the next field).
   */
  private captureFocusedChargeField(): {
    rowIndex: number;
    columnKey: string;
  } | null {
    if (!this.isApOrArChargeContext()) {
      return null;
    }
    const active = document.activeElement;
    if (
      !(active instanceof HTMLElement) ||
      !this.elementRef.nativeElement.contains(active)
    ) {
      return null;
    }
    const rowElement = active.closest('mat-row') as HTMLElement | null;
    if (!rowElement) {
      return null;
    }
    const renderedRowCount =
      this.elementRef.nativeElement.querySelectorAll('mat-row').length;
    if (renderedRowCount !== this.getRows().length) {
      return null; // Rows were added/removed; the caller sets focus (e.g. Add / Tab).
    }
    const rowIndex = this.getDomRowIndex(rowElement);
    const columnKey = this.getActiveChargeColumnKey(active, rowElement);
    return rowIndex >= 0 && columnKey ? { rowIndex, columnKey } : null;
  }

  /** Re-focus the same row/column after the re-render, only if focus was actually lost. */
  private restoreFocusedChargeField(target: {
    rowIndex: number;
    columnKey: string;
  }): void {
    requestAnimationFrame(() => {
      const active = document.activeElement;
      const focusStillInTable =
        active instanceof HTMLElement &&
        active !== document.body &&
        active.isConnected &&
        this.elementRef.nativeElement.contains(active);
      if (focusStillInTable) {
        return;
      }
      if (active instanceof HTMLElement && active !== document.body) {
        return; // User moved focus elsewhere on the page.
      }
      const rowElement = this.elementRef.nativeElement.querySelectorAll(
        'mat-row'
      )[target.rowIndex] as HTMLElement | undefined;
      const field = rowElement?.querySelector<HTMLElement>(
        `[formcontrolname="${target.columnKey}"]`
      );
      field?.focus();
    });
  }

  public fetchWidth(event: MouseEvent, fieldName: string, rowIndex?: number) {
    event.stopPropagation();
    event.preventDefault();
    const tooltipKey = this.getInlineErrorTooltipKey(fieldName, rowIndex);

    this.commonService.setErrorTooltipData(
      event,
      tooltipKey,
      this.setInlineCrudTableWidth,
      this.setInlineCrudTableTooltipEvent,
      this.setInlineCrudTableErrorTooltip
    );
  }

  public isInlineErrorTooltipOpen(
    fieldName: string,
    rowIndex: number
  ): boolean {
    return !!this.setInlineCrudTableErrorTooltip[
      this.getInlineErrorTooltipKey(fieldName, rowIndex)
    ];
  }

  public getInlineErrorTooltipWidth(
    fieldName: string,
    rowIndex: number
  ): string {
    if (fieldName === 'priority') {
      return '120px';
    }
    return (
      this.setInlineCrudTableWidth[
        this.getInlineErrorTooltipKey(fieldName, rowIndex)
      ] ?? this.setInlineCrudTableWidth[fieldName]
    );
  }

  public closeInlineErrorTooltip(fieldName: string, rowIndex: number): void {
    this.commonService.closeErrorTooltip(
      this.getInlineErrorTooltipKey(fieldName, rowIndex),
      this.setInlineCrudTableErrorTooltip
    );
  }

  private getInlineErrorTooltipKey(
    fieldName: string,
    rowIndex?: number
  ): string {
    return rowIndex == null ? fieldName : `${fieldName}_${rowIndex}`;
  }

  public checkControlHasError(formIndex: number, controlName: string): boolean {
    const rowCtrl = this.getRows()?.at(formIndex);
    const ctl = rowCtrl?.get(controlName);
    return !!(ctl?.touched && ctl?.errors);
  }

  public priorityFieldTooltipStatus(controlId: string, index: number) {
    if (controlId === formFieldIds.priority) {
      this.isOpened[index] = !(this.isOpened[index] ?? false);
    }
  }

  public getErrorMessage(formIndex: number, controlName: string) {
    const rowCtrl = this.getRows()?.at(formIndex);
    const control = rowCtrl?.get(controlName) ?? null;
    const knownMessage = this.tryGetKnownRowControlErrorMessage(
      control,
      controlName
    );
    if (knownMessage !== null) {
      return knownMessage;
    }
    if (
      controlName === 'shipmentIdentifierValue' &&
      control?.errors &&
      !control.errors['required']
    ) {
      return this.getShipmentIdentifierValueErrorMessage(
        formIndex,
        control.errors
      );
    }
    const fieldErrorMessages = this.errorMessages[controlName];
    if (control?.errors && fieldErrorMessages) {
      const errorKey = Object.keys(control.errors)[0];
      if (fieldErrorMessages[errorKey]) {
        return fieldErrorMessages[errorKey];
      }
    }
    if (control?.errors?.['required']) {
      return this.getRequiredFallbackMessage(controlName);
    }
    return '';
  }

  /** For fields marked required at save time (AP/AR charge grids) without a configured message. */
  private getRequiredFallbackMessage(controlName: string): string {
    const label = this.customiseInlineCrudTableColumns?.find(
      column => column.key === controlName
    )?.label;
    return `${label || controlName} is required`;
  }

  private tryGetKnownRowControlErrorMessage(
    control: AbstractControl | null,
    controlName: string
  ): string | null {
    const errors = control?.errors;
    if (!errors) {
      return null;
    }
    if (
      errors[conditionCheckConstants.autocompleteError] ||
      errors[conditionCheckConstants.invalidDecimal]
    ) {
      return validationErrorMessages.autocompleteError;
    }
    if (errors[conditionCheckConstants.invalidEndNumber]) {
      return validationErrorMessages.invalidEndNumber;
    }
    if (errors[conditionCheckConstants.invalidStartNumber]) {
      return validationErrorMessages.numberRangeStartNumberErrMsg;
    }
    if (errors[conditionCheckConstants.invalidPostalCode]) {
      return validationErrorMessages.invalidPostalCode;
    }
    if (errors[conditionCheckConstants.invalidPostalEnd]) {
      return validationErrorMessages.invalidPostalEnd;
    }
    if (errors[conditionCheckConstants.sameTransitPoint]) {
      return validationErrorMessages.sameTransitPoint;
    }
    if (
      controlName === 'invoiceNumber' &&
      errors[conditionCheckConstants.invalidAPInvoice]
    ) {
      return validationErrorMessages.invalidInvoice('AP');
    }
    if (
      controlName === 'invoiceNumber' &&
      errors[conditionCheckConstants.invalidARInvoice]
    ) {
      return validationErrorMessages.invalidInvoice('AR');
    }
    if (errors[conditionCheckConstants.noFacilitiesForCountry]) {
      return validationErrorMessages.noFacilitiesForCountry;
    }
    if (controlName === 'deliveryZone' && errors['duplicateDeliveryZone']) {
      return validationErrorMessages.deliveryZoneNameDuplicate;
    }
    if (errors[conditionCheckConstants.duplicatePriority]) {
      return validationErrorMessages.duplicatePriority;
    }
    if (errors[conditionCheckConstants.priorityOutOfRange]) {
      return validationErrorMessages.priorityOutOfRange;
    }
    return null;
  }

  public getAutocompleteOptionsList(key: string, rowIndex: number) {
    if (this.externalAutocompleteKeys.includes(key)) {
      return this.getExternalTypeaheadOptions(key, rowIndex);
    }
    if (key === this.formFields.deliveryAgent) {
      return this.filteredDeliveryAgentDataOptions[rowIndex];
    } else if (key === this.formFields.rateScale) {
      return this.filteredRateScaleOptions[rowIndex];
    } else if (key === this.formFields.location) {
      return this.filteredLocationOptions[rowIndex];
    } else if (key === this.formFields.department) {
      return this.filteredDepartmentOptions[rowIndex];
    } else if (key === this.formFields.role) {
      return this.filteredRolesOptions[rowIndex];
    } else if (key === this.formFields.group) {
      return this.filteredGroupOptions[rowIndex];
    } else if (key === this.formFields.ibcChargeCode) {
      return this.filteredIbcChargeCodeOptions[rowIndex];
    } else if (key === this.formFields.agentChargeCode) {
      return this.filteredAgentChargeCodeOptions[rowIndex];
    } else if (key === this.formFields.chargeCode) {
      return this.filteredChargeCodeOptions[rowIndex];
    } else if (key === this.formFields.agentChargeTypes) {
      return this.filteredAgentChargeTypeOptions[rowIndex];
    } else if (key === this.formFields.chargeType) {
      return this.filteredApChargeTypeOptions[rowIndex];
    } else if (key === this.formFields.currency) {
      return this.filteredCurrencyOptions[rowIndex];
    } else {
      return null;
    }
  }

  public checkForEnableOrDisable() {
    if (this.pageMode === ModeIcons.view) {
      const data = this.inlineCrudTableDataSource.data;
      if (!data.length) {
        return true;
      }
      if (data.length === 1) {
        const row0 = data[0];
        if (row0?.isInputRow) {
          return true;
        }
        return row0?.isRowDisable === false;
      }
      return false;
    } else {
      return this.inlineCrudTableDataSource.data.length <= 1;
    }
  }

  public emitCreateSelectionChange() {
    const selectedRows = this.inlineTableSelection.selected;
    this.inlineTableSelectionChange.emit(selectedRows);
  }

  private onBlurAPInvoiceNumber(rowIndex = 0) {
    const invoiceNumber = this.getRows().at(rowIndex).get('invoiceNumber')
      ?.value;
    if (this.commonService.isValidField(invoiceNumber)) {
      this.apChargesService
        .validateInvoiceNumber(invoiceNumber)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (data: any) => {
            if (data.statusCode === StatusCodes.Success && data.data) {
              this.patchAPInvoiceData(data.data, rowIndex);
            } else if (data.statusCode === StatusCodes.SuccessfulRequest) {
              this.handleInvalidAPInvoice(rowIndex);
            }
          },
          error: (error: HttpErrorResponse) => {
            this.errorHandlerService.handleError(error);
          },
        });
    }
  }

  private clearAgentChargeCodeFields(row: AbstractControl): void {
    row.get('agentChargeCode')?.setValue('', { emitEvent: false });
    row.get('agentChargeCodeId')?.setValue(null, { emitEvent: false });
  }

  private patchAPInvoiceData(response: any, rowIndex = 0): void {
    const invoiceData = response[0];
    const row = this.getRows().at(rowIndex);
    if (!row) return;
    row.get('invoiceNumber')?.setErrors(null);
    const patchData: any = {
      apInvoiceId: invoiceData.id || '',
      invoiceNumber: invoiceData.invoiceNumber || '',
      invoiceDate: invoiceData.invoiceDate || '',
      agent: invoiceData.agentId || '',
      incomingChargeCurrencyId: invoiceData.incomingChargeCurrencyId || '',
      conversionRate: invoiceData.conversionRate || '',
    };
    if (invoiceData.agentId) {
      const agent = this.agentData?.find(
        (a: any) => a.id === invoiceData.agentId
      );
      if (agent) {
        patchData.agentId = agent.name;
      } else {
        patchData.agentId = '';
      }
    }
    if (invoiceData.incomingChargeCurrencyId) {
      const currency = this.currencyData?.find(
        (c: any) => c.id === invoiceData.incomingChargeCurrencyId
      );
      if (currency) {
        patchData.incomingChargeCurrency = currency.name;
      } else {
        patchData.incomingChargeCurrency = '';
      }
    }
    row.patchValue(patchData);
    this.clearAgentChargeCodeFields(row);
    row.get('agentChargeCodeDescription')?.setValue('', { emitEvent: false });
    this.setAgentChargeCodeOptionsForRow(rowIndex);
    this.onBlurInlineCrudTableControls(rowIndex);
  }

  private handleInvalidAPInvoice(rowIndex = 0): void {
    const row = this.getRows().at(rowIndex);
    if (row) {
      row.get('invoiceNumber')?.setErrors({ invalidAPInvoice: true });
      this.clearApInvoiceDependentFields(row);
      this.setAgentChargeCodeOptionsForRow(rowIndex);
    }
  }

  private clearApInvoiceDependentFields(row: AbstractControl): void {
    row.patchValue({
      apInvoiceId: '',
      agentId: '',
      agent: '',
      invoiceDate: '',
      incomingChargeCurrency: '',
      incomingChargeCurrencyId: '',
      conversionRate: '',
    });
    this.clearAgentChargeCodeFields(row);
    row.get('agentChargeCodeDescription')?.setValue('', { emitEvent: false });
  }

  private onBlurARInvoiceNumber(rowIndex = 0) {
    const invoiceNumber = this.getRows().at(rowIndex).get('invoiceNumber')
      ?.value;
    if (this.commonService.isValidField(invoiceNumber)) {
      this.arChargesService
        .validateInvoiceNumber(invoiceNumber)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (data: any) => {
            if (data.statusCode === StatusCodes.Success) {
              if (data.statusCode !== StatusCodes.Success) {
                this.handleInvalidARInvoice(rowIndex);
                return;
              }
              if (data.data) {
                this.patchARInvoiceData(data.data, rowIndex);
                return;
              }
              this.showPublishedWarningAndReset(data.message, rowIndex);
            }
          },
          error: (error: HttpErrorResponse) => {
            this.errorHandlerService.handleError(error);
          },
        });
    } else {
      this.clearInvoiceFieldsForEmptyInvoiceNumber(rowIndex);
    }
  }

  private clearInvoiceFieldsForEmptyInvoiceNumber(rowIndex: number): void {
    const row = this.getRows().at(rowIndex);
    if (!row) return;
    row.get('invoiceNumber')?.setErrors(null);
    row.patchValue({
      arInvoiceId: null,
      invoiceDate: '',
      published: popupButtons.No,
    });
    this.arChargesService.applyInvoiceDateState(row);
    this.getTableData();
    this.onBlurInlineCrudTableControls(rowIndex);
  }

  private showPublishedWarningAndReset(
    message: string,
    rowIndex: number
  ): void {
    const dialogRef = this.modalService.openPopup(
      {
        message,
        isFooterRequired: false,
        showCloseIcon: true,
      },
      ModalPopupMessages.Warning,
      MessagePopupComponent,
      popupWidth.deletePopup
    );
    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.resetArInvoiceInputRow(rowIndex);
      });
  }

  private resetArInvoiceInputRow(rowIndex = 0): void {
    const row = this.getRows().at(rowIndex);
    if (!row) return;
    row.get('invoiceNumber')?.setErrors(null);
    row.patchValue({
      arInvoiceId: '',
      invoiceNumber: '',
      invoiceDate: '',
      published: '',
      chargeCode: '',
      chargeCodeId: '',
      chargeAmount: '',
      chargeDescription: '',
    });
    this.arChargesService.applyInvoiceDateState(row);
    this.getTableData();
    this.onBlurInlineCrudTableControls(rowIndex);
  }

  private patchARInvoiceData(response: any, rowIndex = 0): void {
    const invoiceData = response[0];
    const row = this.getRows().at(rowIndex);
    if (!row) return;
    row.get('invoiceNumber')?.setErrors(null);
    const patchData: any = {
      arInvoiceId: invoiceData.id || '',
      invoiceNumber: invoiceData.invoiceNumber || '',
      invoiceDate: invoiceData.invoiceDate || '',
      published: invoiceData.isPublish ? popupButtons.Yes : popupButtons.No,
    };
    row.patchValue(patchData);
    this.arChargesService.applyInvoiceDateState(row);
    this.getTableData();
    this.onBlurInlineCrudTableControls(rowIndex);
  }

  private handleInvalidARInvoice(rowIndex = 0): void {
    const row = this.getRows().at(rowIndex);
    if (row) {
      row.get('invoiceNumber')?.setErrors({ invalidARInvoice: true });
      row.patchValue({
        arInvoiceId: '',
        invoiceNumber: '',
        invoiceDate: '',
        published: '',
      });
      this.arChargesService.applyInvoiceDateState(row);
    }
  }

  public onBlurInlineCrudTableControls(rowIndex: number) {
    this.inlineCrudTableFormValue.emit(
      this.getRows().at(rowIndex).getRawValue()
    );
  }

  public onIdentiferValueFieldBlur(rowIndex: number, controlName: string) {
    const selectedIdentifier = this.getRows()
      .at(rowIndex)
      .get('shipmentIdentifier')?.value;
    const identifierValue = this.getRows().at(rowIndex).get(controlName)?.value;
    if (
      this.commonService.isValidField(selectedIdentifier) &&
      this.commonService.isValidField(identifierValue)
    ) {
      this.invoiceIdentifierValue.emit({
        shipmentIdentifer: selectedIdentifier,
        shipmentIdentifierValue: identifierValue,
        index: rowIndex,
        isFormValid: this.getRows().at(rowIndex).get(controlName)?.valid,
      });
    }
  }

  private getExternalTypeaheadOptions(
    key: string,
    rowIndex: number
  ): Observable<IDropdownResponse[]> | undefined {
    const control = this.getRows().at(rowIndex)?.get(key);
    if (!control) return undefined;
    const data = this.getDropdownResponseList(key);
    const cached = this.externalTypeaheadOptions.get(control);
    if (cached?.data === data) return cached.options;
    const options = this.getFilteredData(control, data, key);
    this.externalTypeaheadOptions.set(control, { data, options });
    return options;
  }

  public getInlineTableAutocompleteFields(key: string) {
    return (
      AutocompleteControls.includes(key) ||
      this.externalAutocompleteKeys.includes(key)
    );
  }

  public getInlineTableInputFormControl(key: string) {
    return (
      InputControls.includes(key) &&
      !this.externalAutocompleteKeys.includes(key)
    );
  }

  public getPostalCodeKeys(key: string) {
    return PostalCodeKeys.includes(key);
  }

  public getPalletsKeys(key: string) {
    return PalletsKeys.includes(key);
  }

  public getMarkUpKey(key: string) {
    return MarkUpKey.includes(key);
  }

  public getChargeAmountKey(key: string) {
    return ChargeAmountKey.includes(key);
  }

  public getIdentifierValueKey(key: string) {
    return IdentifierKey.includes(key);
  }

  public getChargeAmountKeys(key: string) {
    return ChargeAmountKeys.includes(key);
  }

  public isShipmentIdentifierValueHyperlink(
    key: string,
    element: any,
    rowIndex?: number
  ): boolean {
    if (key !== 'shipmentIdentifierValue') {
      return false;
    }
    // AR Information page shows identifier values as plain fields (PAC-24771).
    if (this.isFrom === pageType.arChargesForARModule) {
      return false;
    }

    if (
      !element.shipmentIdentifierValue ||
      element.shipmentIdentifierValue.trim() === ''
    ) {
      return false;
    }

    if (!this.getEffectiveIsRowDisable(element, rowIndex)) {
      return false;
    }

    return this.hasValidShipmentId(element);
  }

  private getEffectiveIsRowDisable(element: any, rowIndex?: number): boolean {
    let isRowDisable = element.isRowDisable;
    if (rowIndex !== undefined && rowIndex !== null) {
      const rowControl = this.getRows().at(rowIndex);
      if (rowControl) {
        isRowDisable =
          rowControl.get('isRowDisable')?.value ?? element.isRowDisable;
      }
    }
    return isRowDisable === true;
  }

  private hasValidShipmentId(element: any): boolean {
    const shipmentId = element.shipmentId ?? element.shipmentIdOrMawbId;
    return (
      shipmentId !== null &&
      shipmentId !== undefined &&
      shipmentId !== '' &&
      shipmentId !== '0' &&
      shipmentId !== 0
    );
  }

  public isPlainTextIdentifierDisplay(
    key: string,
    element: any,
    rowIndex?: number
  ): boolean {
    if (
      key !== 'shipmentIdentifierValue' ||
      !element.shipmentIdentifierValue ||
      element.shipmentIdentifierValue.trim() === '' ||
      this.hasValidShipmentId(element)
    ) {
      return false;
    }

    return this.getEffectiveIsRowDisable(element, rowIndex);
  }

  public onShipmentIdentifierValueClick(element: any, rowIndex: number): void {
    if (element.shipmentIdentifierValue) {
      this.shipmentIdentifierValueClick.emit({
        shipmentIdentifier: element.shipmentIdentifier,
        shipmentIdentifierValue: element.shipmentIdentifierValue,
        shipmentId: element.shipmentId ?? element.shipmentIdOrMawbId,
        rowData: element,
        rowIndex: rowIndex,
        isFrom: this.isFrom,
      });
    }
  }

  public isInvoiceNumberHyperlink(key: string, element: any): boolean {
    if (key !== 'invoiceNumber') {
      return false;
    }
    // AR Information page: Invoice Number is a read-only field, not a link (PAC-24771 AC3.4).
    if (this.isFrom === pageType.arChargesForARModule) {
      return false;
    }
    // Hyperlink only in View mode; Add/Edit keeps the disabled field.
    if (this.pageMode !== ModeIcons.view || this.isEditIconClicked) {
      return false;
    }
    if (!element.invoiceNumber || element.invoiceNumber.trim() === '') {
      return false;
    }
    return this.hasValidArInvoiceId(element);
  }

  private hasValidArInvoiceId(element: any): boolean {
    const arInvoiceId = element.arInvoiceId;
    return (
      this.commonService.isValidField(arInvoiceId) &&
      arInvoiceId !== '0' &&
      arInvoiceId !== 0
    );
  }

  public onInvoiceNumberClick(element: any, rowIndex: number): void {
    if (element.invoiceNumber) {
      this.invoiceNumberClick.emit({
        arInvoiceId: element.arInvoiceId,
        invoiceNumber: element.invoiceNumber,
        rowData: element,
        rowIndex: rowIndex,
        isFrom: this.isFrom,
      });
    }
  }

  public shouldApplyNumbersWithHyphenDirective(
    rowIndex: number,
    key: string
  ): boolean {
    if (!this.getIdentifierValueKey(key)) return false;

    const selectedIdentifier = this.getRows()
      .at(rowIndex)
      .get('shipmentIdentifier')?.value;
    const selectedOption = this.shipmentIdentifierData?.find(
      item => item.name === selectedIdentifier
    );
    return selectedOption?.name === hyperlinkLabels.mawb; // MAWB option
  }

  public getDatePickerKeys(key: string) {
    return DatePickerKeys.includes(key);
  }

  public getOtherInputKeys(key: string) {
    return (
      !this.getPostalCodeKeys(key) &&
      !this.getMarkUpKey(key) &&
      !this.getIdentifierValueKey(key) &&
      !this.getChargeAmountKeys(key) &&
      !this.getPalletsKeys(key) &&
      !this.getDatePickerKeys(key)
    );
  }

  public onInputChanges(index: number, controlName?: string) {
    if (
      controlName === 'incomingChargeAmount' &&
      (this.isFrom === selectionTableNames.apChargesForApModule ||
        this.isFrom === selectionTableNames.apChargesForShipmentAndMawb)
    ) {
      this.calculateChargeAmount(index);
      this.onBlurInlineCrudTableControls(index);
    }
    if (this.isFrom === pageType.numberRangesEditSetRange) {
      this.handleNumberRangeChange(index, controlName);
    } else if (this.isFrom === pageType.deliveryZoneInfoTable) {
      this.handlePostalCodeChange(index, controlName);
    } else if (
      this.isFrom === pageType.apChargeCodeInfoTable ||
      this.isFrom === pageType.arChargesForARModule
    ) {
      this.onBlurInlineCrudTableControls(index);
    } else if (this.isFrom === pageType.mawbPallet) {
      this.calculateDimensionaWeight(index, controlName);
      this.onBlurInlineCrudTableControls(index);
    } else if (this.isFrom === selectionTableNames.customerService) {
      this.onBlurInlineCrudTableControls(index);
    }
  }

  private setupPalletRow(rowIndex: number) {
    if (this.isFrom !== pageType.mawbPallet) return;
    const row = this.getRows().at(rowIndex);
    row.get('dimensionalFactor')?.disable();
    row.get('dimensionalWeight')?.setValue('');
  }

  private calculateDimensionaWeight(rowIndex: number, controlName?: string) {
    const row = this.getRows().at(rowIndex);
    if (!row || controlName === 'dimensionalWeight') return;
    const [dimL, dimW, dimH, dimWF] = PalletsKeys.slice(0, 4).map(key =>
      Number.parseFloat(row.get(key)?.value || '0')
    );
    if (dimL > 0 && dimW > 0 && dimH > 0) {
      row.get('dimensionalFactor')?.enable();
    } else {
      row.get('dimensionalFactor')?.disable();
    }
    if (dimL > 0 && dimW > 0 && dimH > 0 && dimWF > 0) {
      const dimensionalWeight = (dimL * dimW * dimH) / dimWF;
      const roundedWeight = Math.round(dimensionalWeight * 1000) / 1000;
      row.get('dimensionalWeight')?.setValue(roundedWeight);
    } else {
      row.get('dimensionalWeight')?.setValue('');
    }
  }

  public onInputBlur(index: number, controlName: string) {
    if (
      this.isFrom === pageType.apChargesForApModule &&
      controlName === 'shipmentIdentifierValue'
    ) {
      this.triggerLinehaulValidation(index);
    }
    if (
      this.isFrom === pageType.apChargesForApModule ||
      this.isFrom === pageType.arChargesForARModule
    ) {
      this.onIdentiferValueFieldBlur(index, controlName);
    } else if (
      this.isFrom === selectionTableNames.apChargesForShipmentAndMawb &&
      controlName === 'invoiceNumber'
    ) {
      this.onBlurAPInvoiceNumber(index);
    } else if (
      this.isFrom === selectionTableNames.arChargesForShipmentAndMawb &&
      controlName === 'invoiceNumber'
    ) {
      this.onBlurARInvoiceNumber(index);
    } else if (
      this.isFrom === pageType.numberRangesEditSetRange &&
      controlName === 'startNumber'
    ) {
      // Validate startNumber on blur for number ranges
      this.handleNumberRangeChange(index, controlName);
    }
    this.onBlurInlineCrudTableControls(index);
  }

  public onShipmentIndentifierChange(event: MatSelectChange, rowIndex: number) {
    const shipmentIdentifierId = this.shipmentIdentifierData
      .filter(item => event.value === item.name)
      .map(item => item.id);
    if (this.isFrom === selectionTableNames.apChargesForApModule) {
      this.getRows()
        .at(rowIndex)
        .get('shipmentIdentifierId')
        ?.setValue(shipmentIdentifierId?.[0]);
    } else {
      this.getRows()
        .at(rowIndex)
        .get('masterLookupDetailId')
        ?.setValue(shipmentIdentifierId?.[0]);
    }
    this.onBlurInlineCrudTableControls(rowIndex);
    this.setValidationForIdentifierValue(rowIndex, event.value);
    this.triggerLinehaulValidation(rowIndex);
  }

  private setValidationForIdentifierValue(
    rowIndex: number,
    selectedIdentifier: string
  ) {
    const control = this.getRows().at(rowIndex).get('shipmentIdentifierValue');
    if (!control) return;
    control.clearValidators();
    control.setErrors(null);
    const selectedOption = this.shipmentIdentifierData?.find(
      item => item.name === selectedIdentifier
    );
    const identifierName = selectedOption?.name;
    if (
      selectedIdentifier?.toLowerCase() === miscellaneousCheck.toLowerCase()
    ) {
      control.disable();
      control.setValue('');
      return;
    } else {
      control.enable();
    }
    if (identifierName === hyperlinkLabels.hawb) {
      const validators = [
        Validators.minLength(3),
        Validators.maxLength(126),
        this.createAlphaNumericWithUnderscoreValidator(),
      ];
      control.setValidators(validators);
      this.setupInputRestriction(
        control,
        'allowAlphaNumericWithUnderscoreOnly',
        126
      );
    } else if (identifierName === hyperlinkLabels.mawb) {
      const validators = [
        Validators.minLength(11),
        Validators.maxLength(11),
        Validators.pattern(/^\d{11}$/), // Only 11 digits
      ];
      control.setValidators(validators);
      this.setupInputRestriction(control, 'allowNumeric', 11);
    } else if (identifierName === hyperlinkLabels.agentShipmentTracker) {
      const validators = [Validators.maxLength(126)];
      control.setValidators(validators);
      this.setupInputRestriction(control, 'basic', 126);
    }
    control.updateValueAndValidity();
  }

  private handleNumberRangeChange(index: number, controlName?: string) {
    if (controlName !== 'startNumber' && controlName !== 'endNumber') return;

    const startNumber = this.getControlValue(index, 'startNumber');
    const endNumber = this.getControlValue(index, 'endNumber');

    // Validate startNumber for AR Invoice - run validation on every input change
    if (controlName === 'startNumber') {
      const startNumberControl = this.getRows().at(index).get('startNumber');
      if (startNumberControl) {
        // Add or remove custom validator based on AR Invoice selection
        this.updateStartNumberValidator(startNumberControl, index);

        // Force validation to run immediately
        startNumberControl.updateValueAndValidity({ emitEvent: false });

        // Mark as touched if there's an error to show it immediately
        if (startNumberControl.errors?.['invalidStartNumber']) {
          startNumberControl.markAsTouched();
        }
      }
    }

    if (
      this.commonService.isValidField(startNumber) &&
      this.commonService.isValidField(endNumber)
    ) {
      const error =
        +startNumber === +endNumber || +startNumber > +endNumber
          ? { invalidEndNumber: true }
          : null;
      this.getRows().at(index).get('endNumber')?.setErrors(error);
    }

    this.inlineCrudTableFormValueChange = true;
  }

  private updateStartNumberValidator(
    control: AbstractControl,
    index: number
  ): void {
    const isARInvoice =
      this.parentFormValues?.numberRangeTypeName === 'AR Invoice';

    // Get the form config to rebuild validators
    const startNumberField = this.formConfig.fields.find(
      (f: any) => f.id === 'startNumber'
    );
    if (!startNumberField) return;

    // Build base validators from form config
    const baseValidators: ValidatorFn[] = [];
    if (startNumberField.required) {
      baseValidators.push(Validators.required);
    }
    if (startNumberField.minValue) {
      baseValidators.push(Validators.minLength(startNumberField.minValue));
    }
    if (startNumberField.maxValue) {
      baseValidators.push(Validators.maxLength(startNumberField.maxValue));
    }

    // Add AR Invoice validator if needed
    if (isARInvoice) {
      baseValidators.push(this.arInvoiceStartNumberValidator);
    }

    // Set validators
    control.setValidators(baseValidators.length > 0 ? baseValidators : null);

    // Clear invalidStartNumber error if not AR Invoice
    if (!isARInvoice) {
      const errors = control.errors;
      if (errors?.['invalidStartNumber']) {
        const { invalidStartNumber, ...remainingErrors } = errors;
        control.setErrors(
          Object.keys(remainingErrors).length ? remainingErrors : null
        );
      }
    }
  }

  private readonly arInvoiceStartNumberValidator: ValidatorFn = (
    control: AbstractControl
  ): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }
    const numValue = Number(control.value);
    if (Number.isNaN(numValue)) {
      return null;
    }
    if (numValue < 600001) {
      return { invalidStartNumber: true };
    }

    return null;
  };

  private handlePostalCodeChange(index: number, controlName?: string) {
    if (controlName !== 'postalStart' && controlName !== 'postalEnd') return;

    const postalCodeValue = this.getControlValue(index, controlName);

    if (this.commonService.isValidField(postalCodeValue)) {
      const len = String(postalCodeValue).length;
      if (len < 4 || len > 10) {
        return;
      }
    }

    if (
      this.commonService.isValidField(postalCodeValue) &&
      this.commonService.validatePostalCode(postalCodeValue)
    ) {
      this.getRows()
        .at(index)
        .get(controlName)
        ?.setErrors({ invalidPostalCode: true });
    }

    const postalStart = this.getControlValue(index, 'postalStart');
    const postalEnd = this.getControlValue(index, 'postalEnd');

    if (
      this.commonService.isValidField(postalStart) &&
      this.commonService.isValidField(postalEnd)
    ) {
      const error =
        +postalStart === +postalEnd || +postalStart > +postalEnd
          ? { invalidPostalEnd: true }
          : null;
      this.getRows().at(index).get('postalEnd')?.setErrors(error);
    }

    this.onBlurInlineCrudTableControls(index);
  }

  private getControlValue(index: number, controlName: string): any {
    return this.getRows().at(index).get(controlName)?.value;
  }

  private createAlphaNumericWithUnderscoreValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      const value = control.value.toString();
      // Allow alphabets, numbers, and underscore only
      const regex = /^\w+$/;
      return regex.test(value) ? null : { invalidCharacters: true };
    };
  }

  private setupInputRestriction(
    control: AbstractControl,
    type: string,
    maxLength: number
  ): void {
    // Clear any existing subscriptions to prevent multiple subscriptions
    if ((control as any)._inputRestrictionSubscription) {
      (control as any)._inputRestrictionSubscription.unsubscribe();
    }

    (control as any)._inputRestrictionSubscription = control.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        if (
          typeof value === 'string' &&
          value !== null &&
          value !== undefined
        ) {
          let sanitizedValue = value;

          switch (type) {
            case 'allowAlphaNumericWithUnderscoreOnly':
              // Allow alphabets, numbers, and underscore only
              sanitizedValue = value.replace(/[^\w]/g, '');
              break;
            case 'allowNumeric':
              // Allow numbers only
              sanitizedValue = value.replace(/\D/g, '');
              break;
            case 'basic':
              // No specific restriction, just length
              break;
          }

          // Apply max length restriction - this will prevent typing beyond the limit
          const trimmedValue = sanitizedValue.slice(0, maxLength);

          if (value !== trimmedValue) {
            control.setValue(trimmedValue, { emitEvent: false });
          }
        }
      });
  }

  private getShipmentIdentifierValueErrorMessage(
    formIndex: number,
    errors: ValidationErrors
  ): string {
    const selectedIdentifier = this.getRows()
      .at(formIndex)
      .get('shipmentIdentifier')?.value;

    const selectedOption = this.shipmentIdentifierData?.find(
      item => item.name === selectedIdentifier
    );

    const identifierName = selectedOption?.name;

    if (identifierName === hyperlinkLabels.mawb)
      return this.getMawbErrorMessage(errors);
    if (identifierName === hyperlinkLabels.hawb)
      return this.getHawbErrorMessage(errors);
    if (
      identifierName ===
      eventDetailsPropertyKeyDisplayProps['agentShipmentTracker']
    )
      return this.getAgentShipmentErrorMessage(errors);

    return 'Invalid input';
  }

  private getHawbErrorMessage(errors: ValidationErrors): string {
    if (errors['minlength'] || errors['maxlength']) {
      return 'Shipment field should have 3 to 126 alphanumeric characters';
    }
    if (errors['invalidCharacters']) {
      return 'Shipment field should contain only alphanumeric characters and underscores';
    }
    if (errors?.[conditionCheckConstants.arInvoiceBlockedCannotAdd]) {
      return ModalPopupMessages.arInvoiceBlockedCannotAdd;
    }
    if (errors?.[conditionCheckConstants.invalidShipmentExt]) {
      return validationErrorMessages.invalidShipmentExt;
    }
    if (errors?.[conditionCheckConstants.invalidShipmentAutoRate]) {
      return validationErrorMessages.invalidShipmentAutoRate;
    }
    return 'Invalid input';
  }

  private getMawbErrorMessage(errors: ValidationErrors): string {
    if (errors['minlength'] || errors['maxlength']) {
      return 'MAWB field should have exactly 11 digits';
    }
    if (errors['pattern']) {
      return 'MAWB field should contain only 11 numeric digits';
    }
    const mawbBlocked = errors?.[conditionCheckConstants.mawbBlockedArInvoice];
    if (mawbBlocked) {
      return typeof mawbBlocked === 'string'
        ? mawbBlocked
        : validationErrorMessages.invalidMawbExt;
    }
    if (errors?.[conditionCheckConstants.invalidMawbExt]) {
      return validationErrorMessages.invalidMawbExt;
    }
    return 'Invalid input';
  }

  private getAgentShipmentErrorMessage(errors: ValidationErrors): string {
    if (errors['maxlength']) {
      return 'Agent Shipment Tracker field should have maximum 126 characters';
    }
    if (errors?.[conditionCheckConstants.invalidAgentNumberExt]) {
      return validationErrorMessages.invalidAgentNumberExt;
    }
    return 'Invalid input';
  }

  private cleanupInputRestrictionSubscriptions(): void {
    if (!this.inlineCrudTableForm) {
      return;
    }
    const rows = this.inlineCrudTableForm.get('rows') as FormArray;
    if (!rows) {
      return;
    }
    for (let i = 0; i < rows.length; i++) {
      const control = rows.at(i).get('shipmentIdentifierValue');
      if (control && (control as any)._inputRestrictionSubscription) {
        (control as any)._inputRestrictionSubscription.unsubscribe();
        delete (control as any)._inputRestrictionSubscription;
      }
    }
  }

  public clearLookupFieldValues(controlName: string, formIndex: number) {
    const rows = this.getRows();
    const control = rows.at(formIndex).get(controlName);
    control?.setValue('');
    if (
      controlName === formFieldIds.countryCode &&
      this.isFrom === selectionTableNames.DeliveryZone
    ) {
      const originControl = rows.at(formIndex).get(formFieldIds.origin);
      originControl?.setValue('');
      rows.at(formIndex).get('originId')?.setValue('');
      originControl?.disable();
    }
    this.onBlurInlineCrudTableControls(formIndex);
  }

  public onInlineSelectCleared(controlName: string, rowIndex: number): void {
    const row = this.getRows().at(rowIndex);
    if (controlName === 'shipmentIdentifier') {
      this.commonService.clearCompanionControls(row, 'shipmentIdentifierId');
      this.onShipmentIndentifierChange(
        { value: row.get(controlName)?.value } as MatSelectChange,
        rowIndex
      );
    } else if (controlName === apChargeCodeFields.agentChargeCode) {
      this.commonService.clearCompanionControls(
        row,
        apChargeCodeFields.agentChargeCodeId
      );
      this.onAgentChargeCodeSelectionChange(
        { value: row.get(controlName)?.value } as MatSelectChange,
        rowIndex
      );
    }
    this.onBlurInlineCrudTableControls(rowIndex);
  }

  public onInlineAutocompleteCleared(
    controlName: string,
    rowIndex: number
  ): void {
    const row = this.getRows().at(rowIndex);
    this.commonService.clearCompanionControls(
      row,
      this.getApiControlNames(controlName)
    );
    this.formAutocompleteValueChanges(controlName, rowIndex);
    this.updateModuleAndControlBasedActions(controlName, rowIndex);
  }

  public isOriginFieldEnabled(rowIndex: number): boolean {
    return !this.getRows().at(rowIndex).get(formFieldIds.origin)?.disabled;
  }

  public shouldShowOriginIcons(rowIndex: number): boolean {
    const row = this.getRows().at(rowIndex);
    return (
      !row.get('isRowDisable')?.value && !row.get(formFieldIds.origin)?.disabled
    );
  }

  public isCountryFieldEnabled(rowIndex: number): boolean {
    return !this.getRows().at(rowIndex).get(formFieldIds.countryCode)?.disabled;
  }

  public shouldShowCountryIcons(rowIndex: number): boolean {
    const row = this.getRows().at(rowIndex);
    return (
      !row.get('isRowDisable')?.value &&
      !row.get(formFieldIds.countryCode)?.disabled
    );
  }

  public onLookupSearchIconClicked(controlName: string, formIndex: number) {
    const rows = this.getRows();
    const control = rows.at(formIndex).get(controlName);

    if (controlName === formFieldIds.origin) {
      const countryCode = this.extractCountryCode(rows.at(formIndex));
      if (countryCode) {
        this.fetchFacilitiesAndOpenLookup(
          countryCode,
          control,
          rows,
          formIndex
        );
      }
      return;
    }

    const lookupData = this.countryList;
    if (lookupData) {
      this.openLookupAndHandleSelection(
        controlName,
        lookupData,
        control,
        rows,
        formIndex
      );
    }
  }

  private extractCountryCode(row: AbstractControl): string {
    return row.get('country')?.value || '';
  }

  private fetchFacilitiesAndOpenLookup(
    countryCode: string,
    control: AbstractControl | null,
    rows: FormArray,
    formIndex: number
  ) {
    this.commonService
      .getFacilitiesUsingCountry(countryCode)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          if (response?.data?.length > 0) {
            control?.setErrors(null);
            this.openLookupAndHandleSelection(
              formFieldIds.origin,
              response.data,
              control,
              rows,
              formIndex
            );
          } else {
            control?.setErrors({
              [conditionCheckConstants.noFacilitiesForCountry]: true,
            });
            control?.markAsTouched();
          }
        },
        error: (error: HttpErrorResponse) => {
          this.errorHandlerService.handleError(error);
        },
      });
  }

  private openLookupAndHandleSelection(
    controlName: string,
    lookupData: IDropdownResponse[],
    control: AbstractControl | null,
    rows: FormArray,
    formIndex: number
  ) {
    const dialogRef = this.commonPopupService.openLookupPopup(
      this.modalService,
      controlName,
      lookupData,
      control?.value
    );
    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((popupData: any) => {
        if (popupData?.data) {
          this.handleLookupSelection(
            controlName,
            popupData.data,
            control,
            rows,
            formIndex
          );
        }
        control?.markAsDirty();
        this.onBlurInlineCrudTableControls(formIndex);
      });
  }

  private handleLookupSelection(
    controlName: string,
    data: any,
    control: AbstractControl | null,
    rows: FormArray,
    formIndex: number
  ) {
    if (controlName === formFieldIds.countryCode) {
      control?.setValue(data.country);
      rows
        .at(formIndex)
        .get('country')
        ?.setValue(data.country?.split(' - ')[0]);
      if (this.isFrom === selectionTableNames.DeliveryZone) {
        this.clearOriginOnCountryChange(rows, formIndex);
        rows.at(formIndex).get(formFieldIds.origin)?.enable();
      }
    } else if (controlName === formFieldIds.origin) {
      control?.setValue(data.name);
      rows.at(formIndex).get('originId')?.setValue(data.id);
    }
  }

  private clearOriginOnCountryChange(rows: FormArray, formIndex: number) {
    const originControl = rows.at(formIndex).get(formFieldIds.origin);
    originControl?.setValue('');
    originControl?.setErrors(null);
    rows.at(formIndex).get('originId')?.setValue('');
  }

  public restrictTypingForPriority(index: number, event: KeyboardEvent) {
    if (
      event.key.length === 1 &&
      !/\d/.test(event.key) &&
      !event.ctrlKey &&
      !event.metaKey &&
      !event.altKey
    ) {
      event.preventDefault();
      return;
    }
    if (event.key.length === 1 && /\d/.test(event.key)) {
      const input = event.target as HTMLInputElement | null;
      const current =
        input?.value ??
        String(this.getRows().at(index).get('priority')?.value ?? '');
      const start = input?.selectionStart ?? current.length;
      const end = input?.selectionEnd ?? current.length;
      const nextValue = `${current.slice(0, start)}${event.key}${current.slice(
        end
      )}`;
      const numericValue = Number(nextValue);
      if (
        !nextValue ||
        nextValue.length > 2 ||
        Number.isNaN(numericValue) ||
        numericValue < 1 ||
        numericValue > 10
      ) {
        event.preventDefault();
        return;
      }
    }
    this.inlineCrudTableFormValueChange = true;
  }

  public onPriorityInput(_index: number): void {
    if (this.isFrom !== pageType.numberRangesEditSetRange) {
      return;
    }
    this.validatePriorityRules();
    this.inlineCrudTableFormValueChange = true;
  }

  private validatePriorityRules(): void {
    const rows = this.getRows();
    const priorityCounts = new Map<string, number>();
    const minPriority = 1;
    const maxPriority = 10;

    for (let i = 0; i < rows.length; i++) {
      const raw = rows.at(i).get('priority')?.value;
      if (!this.commonService.isValidField(raw)) {
        continue;
      }
      const numericValue = Number(raw);
      if (
        Number.isNaN(numericValue) ||
        numericValue < minPriority ||
        numericValue > maxPriority
      ) {
        continue;
      }
      const key = String(numericValue);
      priorityCounts.set(key, (priorityCounts.get(key) ?? 0) + 1);
    }

    for (let i = 0; i < rows.length; i++) {
      const control = rows.at(i).get('priority');
      if (!control) {
        continue;
      }
      const errors = { ...(control.errors ?? {}) };
      delete errors[conditionCheckConstants.duplicatePriority];
      delete errors[conditionCheckConstants.priorityOutOfRange];

      const raw = control.value;
      const hasValue = this.commonService.isValidField(raw);
      const numericValue = hasValue ? Number(raw) : NaN;
      const isOutOfRange =
        hasValue &&
        (Number.isNaN(numericValue) ||
          numericValue < minPriority ||
          numericValue > maxPriority);
      const key =
        hasValue && !Number.isNaN(numericValue) ? String(numericValue) : '';
      const isDuplicate =
        !isOutOfRange && !!key && (priorityCounts.get(key) ?? 0) > 1;

      if (isOutOfRange) {
        errors[conditionCheckConstants.priorityOutOfRange] = true;
      } else if (isDuplicate) {
        errors[conditionCheckConstants.duplicatePriority] = true;
      } else {
        this.closeInlineErrorTooltip('priority', i);
      }

      const nextErrors = Object.keys(errors).length ? errors : null;
      control.setErrors(nextErrors);
      if (isOutOfRange || isDuplicate) {
        control.markAsTouched();
        this.isOpened[i] = false;
      }
    }
  }

  public isMaximumRangesAdded() {
    if (this.isFrom !== pageType.numberRangesEditSetRange) {
      return (
        this.inlineCrudTableDataSource.data.filter(
          (row: any) => row.id || row.tempId
        ).length < 5
      );
    }
    return (
      this.inlineCrudTableDataSource.data.filter(
        (row: any) => row.id || row.tempId
      ).length < 10
    );
  }

  public addNewNumberRangeDetails() {
    if (this.isFrom === pageType.numberRangesEditSetRange) {
      this.validatePriorityRules();
    }
    if (this.inlineCrudTableForm.valid) {
      this.addNewRow();
      const newRow = this.getRows().at(0).value;
      newRow.tempId = this.commonService.generateUniqueTempId();
      newRow.action = apiCrudActions.create;
      newRow.isInputRow = false;
      this.getRows()
        .at(this.getRows().length - 1)
        .patchValue(newRow);
      this.getRows().at(0).reset();
      this.getRows().at(0).patchValue(setRangeInputRowData[0]);
      // Set default value and validator for startNumber if AR Invoice is selected
      const startNumberControl = this.getRows().at(0).get('startNumber');
      if (this.parentFormValues?.numberRangeTypeName === 'AR Invoice') {
        startNumberControl?.setValue('600001');
        if (startNumberControl) {
          this.updateStartNumberValidator(startNumberControl, 0);
        }
      } else if (startNumberControl) {
        this.updateStartNumberValidator(startNumberControl, 0);
      }
      this.getRows().at(0).markAsUntouched();
      this.getRows().at(0).updateValueAndValidity();
      if (this.isFrom === pageType.numberRangesEditSetRange) {
        this.validatePriorityRules();
        this.getTableData();
        this.inlineCrudTableFormValueChange = true;
        this.cdRef.detectChanges();
        this.focusStartNumberAfterAdd();
      } else {
        this.getTableData();
        this.inlineCrudTableFormValueChange = true;
      }
    } else {
      this.inlineCrudTableForm.markAllAsTouched();
    }
  }

  private focusStartNumberAfterAdd(): void {
    requestAnimationFrame(() => {
      setTimeout(() => {
        const input = this.elementRef.nativeElement.querySelector(
          'input[data-nr-start="true"]'
        ) as HTMLInputElement | null;
        if (input && !input.disabled) {
          input.focus({ preventScroll: false });
          input.select();
          return;
        }
        this.focusField(0, 'startNumber');
      }, 0);
    });
  }

  public deleteNumberRangeDetails(rowData: any) {
    const param = rowData.id === 0 ? selectedIdForSummaryTable.tempId : 'id';
    if (
      rowData.id &&
      rowData.lastUsedNumber !== '' &&
      rowData.lastUsedNumber !== 0
    ) {
      this.modalService.openPopup(
        {
          message: validationErrorMessages.usedNumberRangeDelete(''),
          isFooterRequired: false,
        },
        ModalPopupMessages.Warning,
        MessagePopupComponent,
        popupWidth.deletePopup
      );
      return;
    }
    const rows = this.getRows();
    const indexToRemove: number[] = [];
    rows.controls.forEach((control, index) => {
      if (control.value[param] === rowData[param]) {
        indexToRemove.push(index);
      }
    });
    indexToRemove.forEach(index => {
      if (param === selectedIdForSummaryTable.Id) {
        rows.at(index).get('action')?.setValue(ActionIcons.delete);
      }
      rows.removeAt(index);
    });
    if (this.isFrom === pageType.numberRangesEditSetRange) {
      this.validatePriorityRules();
    }
    this.getTableData();
    this.inlineCrudTableFormValueChange = true;
  }

  private createFormArray() {
    this.inlineCrudTableForm = this.fb.group({
      rows: this.fb.array([]),
    });
    this.addNewRow();
    this.getRows().at(0).get('id')?.setValue(0);
    this.getRows().at(0).get('action')?.setValue('create');
    this.getRows()
      .at(0)
      .get('tempId')
      ?.setValue(this.commonService.generateUniqueTempId());
    this.getRows().at(0).get('isRowDisable')?.setValue(false);
    if (this.isFrom === pageType.numberRangesEditSetRange) {
      this.getRows().at(0).get('isInputRow')?.setValue(true);
      // Apply validator if AR Invoice is already selected
      const startNumberControl = this.getRows().at(0).get('startNumber');
      if (
        startNumberControl &&
        this.parentFormValues?.numberRangeTypeName === 'AR Invoice'
      ) {
        this.updateStartNumberValidator(startNumberControl, 0);
      }
    } else if (this.isFrom === pageType.apChargesForApModule) {
      this.getRows().at(0).get('weight')?.disable();
      this.getRows().at(0).get('destination')?.disable();
      this.getRows().at(0).get('agentChargeDescription')?.disable();
      this.getRows().at(0).get('shipmentId')?.disable();
    } else if (this.isFrom === pageType.arChargesForARModule) {
      this.getRows().at(0).get('weight')?.disable();
      this.getRows().at(0).get('chargeDescription')?.disable();
      this.getRows().at(0).get('shipmentIdOrMawbId')?.disable();
      // Invoice Number is system-assigned on save (PAC-24771 AC3.4); never user-editable.
      this.getRows().at(0).get('invoiceNumber')?.disable();
    } else if (this.isFrom === pageType.mawbPallet) {
      this.setupPalletRow(0);
    } else if (
      this.isFrom === selectionTableNames.apChargesForShipmentAndMawb
    ) {
      this.getRows().at(0).get('invoiceDate')?.disable();
      this.getRows().at(0).get('incomingChargeCurrency')?.disable();
      this.getRows().at(0).get('agentId')?.disable();
      this.getRows().at(0).get('conversionRate')?.disable();
      this.getRows().at(0).get('arInvoiceNumber')?.disable();
    } else if (
      this.isFrom === selectionTableNames.arChargesForShipmentAndMawb
    ) {
      this.getRows().at(0).get('invoiceDate')?.disable();
      this.getRows().at(0).get('chargeDescription')?.disable();
    } else if (this.isFrom === selectionTableNames.DeliveryZone) {
      this.getRows().at(0).get('origin')?.disable();
    }
  }

  public addNewRow() {
    const newRowIndex = this.getRows().length;
    this.getRows().push(this.getRowFormGroupConfig());
    // Apply validator if AR Invoice is selected and this is a number range table
    if (this.isFrom === pageType.numberRangesEditSetRange) {
      const startNumberControl = this.getRows()
        .at(newRowIndex)
        .get('startNumber');
      if (
        startNumberControl &&
        this.parentFormValues?.numberRangeTypeName === 'AR Invoice'
      ) {
        this.updateStartNumberValidator(startNumberControl, newRowIndex);
      }
    }
  }

  public addNewRowAtTop() {
    this.getRows().insert(0, this.getRowFormGroupConfig());
  }

  public insertNewRowAt(index: number): void {
    this.getRows().insert(index, this.getRowFormGroupConfig());
    this.shiftRowIndexedStateForInsert(index);
    this.applyChargeContextRowDefaults(index);
  }

  /** Keep per-row autocomplete / selection arrays aligned with the FormArray after an insert. */
  private shiftRowIndexedStateForInsert(index: number): void {
    const rowIndexedArrays: unknown[][] = [
      this.filteredDeliveryAgentDataOptions,
      this.filteredRateScaleOptions,
      this.filteredRateScaleOptionsBasedOnAgents,
      this.filteredLocationOptions,
      this.filteredDepartmentOptions,
      this.filteredGroupOptions,
      this.filteredRolesOptions,
      this.filteredIbcChargeCodeOptions,
      this.filteredAgentChargeCodeOptions,
      this.filteredChargeCodeOptions,
      this.filteredAgentChargeTypeOptions,
      this.filteredApChargeTypeOptions,
      this.filteredCurrencyOptions,
      this.selectedDepartmentsData,
      this.selectedRolesData,
      this.selectedLocationsData,
      this.selectedIbcChargeCodeData,
      this.selectedAgentChargeCodeData,
      this.selectedChargeCodeData,
      this.selectedAgentChargeTypeData,
      this.selectedApChargeTypeData,
      this.selectedCurrencyData,
      this.isOpened,
      ...Object.values(this.genericFilteredOptions),
    ];
    rowIndexedArrays.forEach(rowState => {
      if (Array.isArray(rowState) && rowState.length > index) {
        rowState.splice(index, 0, undefined);
      }
    });
  }

  public getFirstFocusableChargeColumnKey(): string {
    if (this.isFrom === selectionTableNames.apChargesForShipmentAndMawb) {
      return 'invoiceNumber';
    }
    if (this.isFrom === selectionTableNames.arChargesForShipmentAndMawb) {
      return 'invoiceNumber';
    }
    return 'shipmentIdentifier';
  }

  private shouldAppendChargeRowOnTab(
    rowIndex: number,
    active: HTMLElement,
    rowElement: HTMLElement
  ): boolean {
    if (this.isLastFocusableInRow(active, rowElement)) {
      return true;
    }
    const activeColumnKey = this.getActiveChargeColumnKey(active, rowElement);
    if (!activeColumnKey) {
      return false;
    }
    const lastColumnKeys = this.getLastFocusableChargeColumnKeys(rowIndex);
    return lastColumnKeys.includes(activeColumnKey);
  }

  private getActiveChargeColumnKey(
    active: HTMLElement,
    rowElement: HTMLElement
  ): string | null {
    let node: HTMLElement | null = active;
    while (node && rowElement.contains(node)) {
      const name = node.getAttribute('formcontrolname');
      if (name) {
        return name;
      }
      node = node.parentElement;
    }
    return null;
  }

  private getLastFocusableChargeColumnKeys(rowIndex: number): string[] {
    const row = this.getRows().at(rowIndex);
    if (!row) {
      return [];
    }
    if (
      this.isFrom === selectionTableNames.apChargesForApModule ||
      this.isFrom === pageType.apChargesForApModule
    ) {
      const agentChargeCode = row.get('agentChargeCode');
      if (agentChargeCode && !agentChargeCode.disabled) {
        return ['agentChargeCode'];
      }
      return ['chargeType'];
    }
    if (
      this.isFrom === selectionTableNames.arChargesForARModule ||
      this.isFrom === pageType.arChargesForARModule
    ) {
      return ['chargeAmount'];
    }
    if (this.isFrom === selectionTableNames.apChargesForShipmentAndMawb) {
      return ['chargeAmount'];
    }
    if (this.isFrom === selectionTableNames.arChargesForShipmentAndMawb) {
      return ['chargeAmount'];
    }
    return [];
  }

  private isApOrArChargeContext(): boolean {
    return (
      this.isFrom === selectionTableNames.apChargesForApModule ||
      this.isFrom === selectionTableNames.arChargesForARModule ||
      this.isFrom === selectionTableNames.apChargesForShipmentAndMawb ||
      this.isFrom === selectionTableNames.arChargesForShipmentAndMawb
    );
  }

  private isChargeKeyboardNavigationActive(): boolean {
    if (!this.isApOrArChargeContext()) {
      return false;
    }
    const rows = this.getRows();
    if (!rows?.length) {
      return false;
    }
    return rows.controls.some(
      control => control.get('isRowDisable')?.value !== true
    );
  }

  private handleChargeTableKeydown(event: Event): void {
    if (!(event instanceof KeyboardEvent)) {
      return;
    }
    if (event.key !== 'Tab' || event.shiftKey) {
      return;
    }
    if (!this.isChargeKeyboardNavigationActive()) {
      return;
    }
    if (isActiveElementInsideCdkOverlay()) {
      return;
    }
    const active = document.activeElement;
    if (!(active instanceof HTMLElement)) {
      return;
    }
    if (!this.elementRef.nativeElement.contains(active)) {
      return;
    }
    const rowElement = active.closest('mat-row') as HTMLElement | null;
    if (!rowElement || !this.elementRef.nativeElement.contains(rowElement)) {
      return;
    }
    const rowIndex = this.getDomRowIndex(rowElement);
    if (rowIndex < 0) {
      return;
    }
    const rowControl = this.getRows().at(rowIndex);
    if (!rowControl || rowControl.get('isRowDisable')?.value === true) {
      return;
    }
    if (!this.shouldAppendChargeRowOnTab(rowIndex, active, rowElement)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.appendChargeRowFromKeyboard.emit({ rowIndex });
  }

  private getDomRowIndex(rowElement: HTMLElement): number {
    const tableRows = Array.from(
      this.elementRef.nativeElement.querySelectorAll('mat-row')
    );
    return tableRows.indexOf(rowElement);
  }

  private getTabbableElementsInRow(rowElement: HTMLElement): HTMLElement[] {
    const selector =
      'input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), mat-select:not([aria-disabled="true"]), .mat-mdc-select-trigger';
    const nodes = Array.from(
      rowElement.querySelectorAll<HTMLElement>(selector)
    );
    const deduped: HTMLElement[] = [];
    for (const node of nodes) {
      if (node.closest('.mat-mdc-autocomplete-panel')) {
        continue;
      }
      const style = globalThis.getComputedStyle(node);
      if (style.display === 'none' || style.visibility === 'hidden') {
        continue;
      }
      if (
        deduped.some(
          existing => existing.contains(node) || node.contains(existing)
        )
      ) {
        continue;
      }
      deduped.push(node);
    }
    return deduped;
  }

  private isLastFocusableInRow(
    active: HTMLElement,
    rowElement: HTMLElement
  ): boolean {
    const tabbables = this.getTabbableElementsInRow(rowElement);
    if (!tabbables.length) {
      return false;
    }
    const activeIndex = tabbables.findIndex(
      el => el === active || el.contains(active)
    );
    return activeIndex === tabbables.length - 1;
  }

  private applyChargeContextRowDefaults(rowIndex: number): void {
    const row = this.getRows().at(rowIndex);
    if (!row) {
      return;
    }
    if (this.isFrom === pageType.apChargesForApModule) {
      row.get('weight')?.disable();
      row.get('destination')?.disable();
      row.get('agentChargeDescription')?.disable();
      row.get('shipmentId')?.disable();
    } else if (this.isFrom === pageType.arChargesForARModule) {
      row.get('weight')?.disable();
      row.get('chargeDescription')?.disable();
      row.get('shipmentIdOrMawbId')?.disable();
    } else if (
      this.isFrom === selectionTableNames.apChargesForShipmentAndMawb
    ) {
      row.get('invoiceDate')?.disable();
      row.get('incomingChargeCurrency')?.disable();
      row.get('agentId')?.disable();
      row.get('conversionRate')?.disable();
      row.get('arInvoiceNumber')?.disable();
    } else if (
      this.isFrom === selectionTableNames.arChargesForShipmentAndMawb
    ) {
      row.get('invoiceDate')?.disable();
      row.get('chargeDescription')?.disable();
    }
  }

  // Generic method to mark a row as saved (can be called from parent via ViewChild)
  public markRowAsSaved(rowIndex: number): void {
    const row = this.getRows().at(rowIndex);
    if (!row) return;
    row.patchValue({
      isRowDisable: true,
      action: apiCrudActions.create,
      tempId: this.commonService.generateUniqueTempId(),
    });
    this.getTableData();
    this.inlineCrudTableFormValueChange = true;
  }

  // Generic method to delete a row (can be called from parent via ViewChild)
  public deleteRow(rowIndex: number): void {
    this.getRows().removeAt(rowIndex);
    this.getTableData();
    this.inlineCrudTableFormValueChange = true;
  }

  private getRowFormGroupConfig() {
    const { form, errorMessages } = this.formUtilsService.generateForm(
      this.formConfig,
      this.destroyRef
    );
    this.errorMessages = errorMessages;
    if (
      this.isFrom === pageType.apChargesForApModule &&
      !form.contains('isLinehaulMismatch')
    ) {
      form.addControl('isLinehaulMismatch', new FormControl(false));
    }
    return form;
  }

  private setRequiredInitialization() {
    this.customiseInlineCrudTableColumns = this.inlineCrudTableCustomizeConfig; // set columns config
    this.inlineTableDisplayedColumns =
      this.displayedColumnsOverride ??
      this.tableService.getDefaultDisplayColumns(this.isFrom); // set headers for table
    this.setTableRowIds(false);
  }

  private setTableRowIds(idParam: boolean) {
    if (
      this.isFrom === pageType.customerService ||
      this.isFrom === pageType.userDepartment ||
      this.isFrom === pageType.accountUsers ||
      this.isFrom === pageType.deliveryZoneInfoTable ||
      this.isFrom === pageType.apChargeCodeInfoTable ||
      this.isFrom === pageType.mawbPallet
    ) {
      this.selectedRowModuleId = idParam
        ? selectedIdForSummaryTable.tempId
        : selectedIdForSummaryTable.Id;
    } else if (
      this.isFrom === pageType.apChargesForApModule ||
      this.isFrom === selectionTableNames.apChargesForShipmentAndMawb
    ) {
      this.selectedRowModuleId = idParam
        ? selectedIdForSummaryTable.invoiceChargesId
        : selectedIdForSummaryTable.tempId;
    } else if (
      this.isFrom === pageType.arChargesForARModule ||
      this.isFrom === selectionTableNames.arChargesForShipmentAndMawb
    ) {
      this.selectedRowModuleId = idParam
        ? selectedIdForSummaryTable.Id
        : selectedIdForSummaryTable.tempId;
    }
  }

  private checkForCreateModeId(row: any) {
    const usesPersistedId =
      this.isFrom === selectionTableNames.mawbPallet
        ? !(Number(row?.id) > 0)
        : !row.id;
    if (
      (this.isFrom === selectionTableNames.customerService ||
        this.isFrom === selectionTableNames.UserDepartment ||
        this.isFrom === selectionTableNames.DeliveryZone ||
        this.isFrom === selectionTableNames.ApChargeCode ||
        this.isFrom === selectionTableNames.mawbPallet) &&
      usesPersistedId
    ) {
      this.setTableRowIds(true);
    }
  }

  private getMasterLookupType(lookupKey: any) {
    this.commonService
      .getMasterLookUp(lookupKey)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: any) => {
          if (data.statusCode !== StatusCodes.Success) {
            this.errorHandlerService.handleApiError(data);
            return;
          }
          this.processMasterLookupData(lookupKey, data.data);
          this.checkForCrudTablePageSidebarExpanded();
        },
        error: (error: HttpErrorResponse) => {
          this.errorHandlerService.handleError(error);
        },
      });
  }

  private processMasterLookupData(lookupKey: any, data: any): void {
    switch (lookupKey) {
      case MasterLookup.deliveryAgent:
        this.processDeliveryAgent(data);
        break;
      case MasterLookup.rateScale:
        this.processRateScale(data);
        break;
      case MasterLookup.countries:
        this.countryList = data;
        break;
      case MasterLookup.ibcChargeCode:
        this.processIbcChargeCode(data);
        break;
      case MasterLookup.agentChargeCode:
        this.processAgentChargeCode(data);
        break;
      case MasterLookup.agentChargeTypes:
        this.processAgentChargeTypes(data);
        break;
      case MasterLookup.agentInfo:
        this.agentData = data;
        break;
      case MasterLookup.currency:
        this.processCurrency(data);
        break;
    }

    if (this.isFrom === selectionTableNames.apChargesForShipmentAndMawb) {
      this.apChargesService.setAgentChargeCodeFieldData(
        this.agentChargeCodeData
      );
      this.apChargesService.setAgentFieldData(this.agentData);
      this.apChargesService.setCurrencyFieldData(this.currencyData);
    }
  }

  private processDeliveryAgent(data: any): void {
    data.forEach((item: IDropdownResponse) => {
      item.name = `${item.code} - ${item.description}`;
    });
    this.deliveryAgentData = data;
    this.autocompleteValueChanges(
      0,
      TableControls.deliveryAgent,
      this.deliveryAgentData
    );
  }

  private processRateScale(data: any): void {
    this.rateScaleData = data;
    this.autocompleteValueChanges(
      0,
      TableControls.rateScale,
      this.rateScaleData
    );
  }

  private processIbcChargeCode(data: any): void {
    if (this.isFrom === selectionTableNames.ApChargeCode) {
      data.forEach(
        (item: IDropdownResponse) =>
          (item.name = `${item.name} - ${item.description}`)
      );
      this.ibcChargeCodeData = data;
      this.autocompleteValueChanges(
        0,
        TableControls.ibcChargeCode,
        this.ibcChargeCodeData
      );
    } else if (
      this.isFrom === selectionTableNames.arChargesForARModule ||
      this.isFrom === selectionTableNames.arChargesForShipmentAndMawb
    ) {
      this.chargeCodeData = data;
      this.autocompleteValueChanges(
        0,
        TableControls.chargeCode,
        this.chargeCodeData
      );
      if (this.isFrom === selectionTableNames.arChargesForShipmentAndMawb) {
        // Opens ArChargesService.dropdownsLoaded$ so the AR tab patches rows,
        // loads unpublished invoices and Sync AP eligibility (PAC-24768).
        this.arChargesService.setChargeCodeFieldData(data);
      }
    }
  }

  private processAgentChargeCode(data: any): void {
    this.originalAgentChargeCodeData = data;
    this.agentChargeCodeData = data;
    if (this.isFrom === selectionTableNames.apChargesForShipmentAndMawb) {
      this.setAgentChargeCodeOptionsForRow(0);
    } else {
      this.autocompleteValueChanges(
        0,
        TableControls.agentChargeCode,
        this.agentChargeCodeData
      );
    }
  }

  private processAgentChargeTypes(data: any): void {
    this.agentChargeTypeData = data;
    this.apChargeTypeData = data;
    this.autocompleteValueChanges(
      0,
      TableControls.agentChargeTypes,
      this.agentChargeTypeData
    );
    this.autocompleteValueChanges(
      0,
      TableControls.apChargeType,
      this.apChargeTypeData
    );
  }

  private processCurrency(data: any): void {
    data.forEach(
      (item: IDropdownResponse) =>
        (item.name = `${item.currencyCode} - ${item.name}`)
    );
    this.currencyData = data;
    this.autocompleteValueChanges(0, TableControls.currency, this.currencyData);
  }

  private getMasterLookups(lookupKey: string) {
    this.commonService
      .getMasterLookups(lookupKey)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: any) => {
          if (data.statusCode == StatusCodes.Success) {
            if (lookupKey === MasterLookup.shipmentIdentifier) {
              if (this.isFrom === selectionTableNames.apChargesForApModule) {
                this.shipmentIdentifierData = this.sortShipmentIdentifierData(
                  data.data
                );
              } else if (
                this.isFrom === selectionTableNames.arChargesForARModule
              ) {
                this.shipmentIdentifierData = data.data.filter(
                  (item: IDropdownResponse) =>
                    item.name === hyperlinkLabels.mawb ||
                    item.name === hyperlinkLabels.hawb
                );
              }
            }
            this.checkForCrudTablePageSidebarExpanded();
          } else {
            this.errorHandlerService.handleApiError(data);
          }
        },
        error: (error: HttpErrorResponse) => {
          this.errorHandlerService.handleError(error);
        },
      });
  }

  private loadRateScalesBasedOnDeliveryAgent(
    selectedAgentId: string,
    rowIndex = 0
  ) {
    this.commonService
      .getRateScalesBasedOnAgents(selectedAgentId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: any) => {
          if (data.statusCode == StatusCodes.Success) {
            this.rateScaleDataBasedOnAgents = data.data;
            this.autocompleteValueChanges(
              rowIndex,
              TableControls.rateScale,
              this.rateScaleDataBasedOnAgents
            );
          } else {
            this.errorHandlerService.handleApiError(data);
          }
        },
        error: (error: HttpErrorResponse) => {
          this.errorHandlerService.handleError(error);
        },
      });
  }

  private sortShipmentIdentifierData(
    data: IDropdownResponse[]
  ): IDropdownResponse[] {
    const orderMap: { [key: string]: number } = {
      Shipment: 1,
      'Agent tracking Number': 2,
      Mawb: 3,
    };
    return data.sort((a, b) => {
      const orderA = orderMap[a.name];
      const orderB = orderMap[b.name];
      return orderA - orderB;
    });
  }

  private getDepartmentAndRoles() {
    this.commonApiService
      .getDepartmentAndRoles()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: any) => {
          if (data.statusCode == StatusCodes.Success) {
            this.departmentsGridData = data.data;
            this.combinationGridData = this.getCombinationData();
            data.data?.department?.forEach((item: any) => {
              item.id = item.deptId;
              item.name = item.deptName;
              item.grpDeptRolesPermissionId =
                item.grp_dept_roles_permissions_id;
              delete item.deptId;
              delete item.deptName;
              delete item.grp_dept_roles_permissions_id;
            });
            const departmentDropdownSource = (
              data.data?.department ?? []
            ).filter((item: any) => this.commonService.isValidField(item.id));
            this.departmentData = departmentDropdownSource.length
              ? this.getUniqueDropdownDataValues(departmentDropdownSource)
              : [];
            data.data?.role?.forEach((item: any) => {
              item.id = item.roleId;
              item.name = item.roleName;
              item.grpDeptRolesPermissionId =
                item.grp_dept_roles_permissions_id;
              delete item.roleId;
              delete item.roleName;
              delete item.grp_dept_roles_permissions_id;
            });
            const roleDropdownSource = (data.data?.role ?? []).filter(
              (item: any) => this.commonService.isValidField(item.id)
            );
            this.rolesData = roleDropdownSource.length
              ? this.getUniqueDropdownDataValues(roleDropdownSource)
              : [];
            data.data?.location?.forEach((item: any) => {
              item.id = item.locationId;
              item.name = this.resolveDepartmentLocationLabel(item);
              item.grpDeptRolesPermissionId =
                item.grp_dept_roles_permissions_id;
              delete item.locationId;
              delete item.locationName;
              delete item.grp_dept_roles_permissions_id;
            });
            this.locationData = data.data.location
              ? this.getUniqueDropdownDataValues(data.data.location)
              : [];
            this.refreshUserDepartmentLocationDropdownsForAllRows();
            this.departmentDropdownsLoaded.emit();
          } else {
            this.errorHandlerService.handleApiError(data);
          }
        },
        error: (error: HttpErrorResponse) => {
          this.errorHandlerService.handleError(error);
        },
      });
  }

  public formAutocompleteValueChanges(controlName: string, rowIndex: number) {
    if (controlName === TableControls.deliveryAgent) {
      this.commonService.getAutocompleteDropdownIdParam(
        this.deliveryAgentData,
        this.getRows().at(rowIndex),
        controlName,
        this.getApiControlNames(controlName)
      );
    } else if (!this.isApInvoiceTypeahead(controlName)) {
      // AP invoice typeahead skips the shared auto-match: it would lock typing on an
      // exact match and replace the text on a single partial match.
      this.commonService.getAutocompleteDropdownId(
        this.getDropdownResponseList(controlName),
        this.getRows().at(rowIndex),
        controlName,
        this.getApiControlNames(controlName)
      );
    }
    this.updateModuleAndControlBasedActions(controlName, rowIndex);
    this.syncApInvoiceFromTypeahead(controlName, rowIndex);
  }

  private isApInvoiceTypeahead(controlName: string): boolean {
    return (
      this.isFrom === selectionTableNames.apChargesForShipmentAndMawb &&
      controlName === TableControls.invoiceNumber &&
      this.externalAutocompleteKeys.includes(controlName)
    );
  }

  /**
   * SPEC-APL-001 AC-APL-003: only picking an option patches Invoice Date / Agent
   * through the existing validate call; typed text clears the dependent fields.
   */
  private syncApInvoiceFromTypeahead(
    controlName: string,
    rowIndex: number,
    isOptionSelected = false
  ) {
    if (!this.isApInvoiceTypeahead(controlName)) return;
    const row = this.getRows().at(rowIndex);
    if (!row) return;
    const invoiceControl = row.get(controlName);
    const invoiceNumber = invoiceControl?.value;
    // Same invoice already filled for this row: no repeat validate call.
    if (this.validatedApInvoices.get(row) === invoiceNumber) return;
    const isExistingInvoice = this.getDropdownResponseList(controlName)?.some(
      (option: IDropdownResponse) => option.name === invoiceNumber
    );
    if (isOptionSelected && isExistingInvoice) {
      this.validatedApInvoices.set(row, invoiceNumber);
      this.onBlurAPInvoiceNumber(rowIndex);
      return;
    }
    this.validatedApInvoices.delete(row);
    this.clearApInvoiceDependentFields(row);
    this.setAgentChargeCodeOptionsForRow(rowIndex);
    if (invoiceNumber) {
      invoiceControl?.setErrors({ invalidAPInvoice: true });
    }
  }

  public loadAutocompleteForNewRow(rowIndex: number) {
    if (this.externalDropdownData) {
      Object.keys(this.externalDropdownData).forEach(key => {
        this.autocompleteValueChanges(
          rowIndex,
          key,
          this.externalDropdownData[key]
        );
      });
    }

    this.autocompleteValueChanges(
      rowIndex,
      TableControls.deliveryAgent,
      this.deliveryAgentData
    );
    this.autocompleteValueChanges(
      rowIndex,
      TableControls.rateScale,
      this.rateScaleData
    );
    this.autocompleteValueChanges(
      rowIndex,
      TableControls.location,
      this.locationData
    );
    this.autocompleteValueChanges(
      rowIndex,
      TableControls.department,
      this.departmentData
    );
    this.autocompleteValueChanges(
      rowIndex,
      TableControls.ibcChargeCode,
      this.ibcChargeCodeData
    );
    if (
      this.isFrom === selectionTableNames.apChargesForShipmentAndMawb &&
      this.originalAgentChargeCodeData?.length
    ) {
      this.setAgentChargeCodeOptionsForRow(rowIndex);
    } else {
      this.autocompleteValueChanges(
        rowIndex,
        TableControls.agentChargeCode,
        this.agentChargeCodeData
      );
    }
    this.autocompleteValueChanges(
      rowIndex,
      TableControls.chargeCode,
      this.chargeCodeData
    );
    this.autocompleteValueChanges(
      rowIndex,
      TableControls.agentChargeTypes,
      this.agentChargeTypeData
    );
    this.autocompleteValueChanges(
      rowIndex,
      TableControls.apChargeType,
      this.apChargeTypeData
    );
    this.autocompleteValueChanges(
      rowIndex,
      TableControls.currency,
      this.currencyData
    );
    this.autocompleteValueChanges(rowIndex, TableControls.role, this.rolesData);
  }

  public onOptionSelectedFormChanges(controlName: string, rowIndex: number) {
    const dropdownList =
      this.isFrom === selectionTableNames.apChargesForShipmentAndMawb &&
      controlName === TableControls.agentChargeCode
        ? this.getAgentChargeCodeListForRow(rowIndex)
        : this.getDropdownResponseList(controlName);
    this.commonService.getAutocompleteDropdownBinding(
      dropdownList,
      this.getRows().at(rowIndex),
      controlName,
      this.getApiControlNames(controlName),
      validationErrorMessages.name
    );
    this.updateModuleAndControlBasedActions(controlName, rowIndex);
    this.syncApInvoiceFromTypeahead(controlName, rowIndex, true);
  }

  private updateModuleAndControlBasedActions(
    controlName: string,
    rowIndex: number
  ) {
    if (
      this.isFrom === pageType.arChargesForARModule &&
      controlName === 'shipmentIdentifierValue' &&
      this.externalAutocompleteKeys.includes(controlName)
    ) {
      // Multi-invoice AR page: identifier picked from the page's shipments behaves like a typed+blurred value.
      this.onIdentiferValueFieldBlur(rowIndex, controlName);
      return;
    }
    if (this.isFrom === selectionTableNames.UserDepartment) {
      this.userDepartmentRowLogic(controlName, rowIndex);
    } else if (this.isChargeCodeControlForAPARModule(controlName)) {
      this.patchChargeDescriptionValue(controlName, rowIndex);
    } else if (
      this.isFrom === selectionTableNames.customerService &&
      controlName === TableControls.deliveryAgent
    ) {
      this.handleDeliveryAgentChange(controlName, rowIndex);
    } else if (
      this.isFrom === selectionTableNames.hawbTransitPoints &&
      controlName === TableControls.outTP
    ) {
      this.validateOutTPNotSameAsInTP(rowIndex);
    }
    this.onBlurInlineCrudTableControls(rowIndex);
  }

  private isChargeCodeControlForAPARModule(controlName: string): boolean {
    const isAPChargeCode =
      (this.isFrom === selectionTableNames.apChargesForApModule ||
        this.isFrom === selectionTableNames.apChargesForShipmentAndMawb) &&
      controlName === TableControls.agentChargeCode;
    const isARChargeCode =
      (this.isFrom === selectionTableNames.arChargesForARModule ||
        this.isFrom === selectionTableNames.arChargesForShipmentAndMawb) &&
      controlName === TableControls.chargeCode;
    return isAPChargeCode || isARChargeCode;
  }

  private handleDeliveryAgentChange(
    controlName: string,
    rowIndex: number
  ): void {
    const deliveryAgentValue = this.getRows().at(rowIndex).get(controlName)
      ?.value;
    const row = this.getRows().at(rowIndex);
    row.get(TableControls.rateScale)?.setValue('');
    row.get(ApiControls.rateScale)?.setValue(null, { emitEvent: false });
    if (!deliveryAgentValue) {
      this.autocompleteValueChanges(
        rowIndex,
        TableControls.rateScale,
        this.rateScaleData
      );
      return;
    }
    const selectedDeliveryAgent = this.deliveryAgentData.find(
      agent => agent.name === deliveryAgentValue
    );
    if (selectedDeliveryAgent) {
      this.loadRateScalesBasedOnDeliveryAgent(
        selectedDeliveryAgent.id,
        rowIndex
      );
    }
  }

  private validateOutTPNotSameAsInTP(rowIndex: number): void {
    const row = this.getRows().at(rowIndex);
    const inTPValue = row.get('inTP')?.value;
    const outTPValue = row.get('outTP')?.value;
    const outTPControl = row.get('outTP');
    const isSameTransitPoint =
      inTPValue && outTPValue && inTPValue === outTPValue;
    outTPControl?.setErrors(
      isSameTransitPoint ? { sameTransitPoint: true } : null
    );
  }

  private calculateChargeAmount(rowIndex: number) {
    const incomingChargeCurrency =
      this.isFrom === selectionTableNames.apChargesForShipmentAndMawb
        ? this.getRows().at(rowIndex).get('incomingChargeCurrency')?.value
        : this.parentFormValues?.incomingChargeCurrency || '';
    const conversionRate =
      this.isFrom === selectionTableNames.apChargesForShipmentAndMawb
        ? this.getRows().at(rowIndex).get('conversionRate')?.value
        : this.parentFormValues?.conversionRate || '';
    const incomingChargeAmount = this.getRows()
      .at(rowIndex)
      .get('incomingChargeAmount')?.value;

    const chargeAmountControl = this.getRows().at(rowIndex).get('chargeAmount');

    if (!this.commonService.isValidField(incomingChargeAmount)) {
      chargeAmountControl?.setValue('', { emitEvent: false });
      return;
    }

    if (
      this.isValidForCalculation(
        incomingChargeCurrency,
        conversionRate,
        incomingChargeAmount
      )
    ) {
      this.calculateAndSetChargeAmount(
        incomingChargeCurrency,
        conversionRate,
        incomingChargeAmount,
        chargeAmountControl
      );
    } else {
      chargeAmountControl?.setValue('', { emitEvent: false });
    }
    this.triggerLinehaulValidation(rowIndex);
  }

  private isValidForCalculation(
    incomingChargeCurrency: string,
    conversionRate: string,
    incomingChargeAmount: any
  ): boolean {
    return (
      this.commonService.isValidField(incomingChargeCurrency) &&
      this.commonService.isValidField(conversionRate) &&
      this.commonService.isValidField(incomingChargeAmount) &&
      conversionRate !== '0' &&
      Number.parseFloat(conversionRate) > 0
    );
  }

  private calculateAndSetChargeAmount(
    incomingChargeCurrency: string,
    conversionRate: string,
    incomingChargeAmount: any,
    chargeAmountControl: AbstractControl | null
  ): void {
    const currencyCode = this.extractCurrencyCode(incomingChargeCurrency);

    if (currencyCode === 'USD') {
      this.setUsdChargeAmount(incomingChargeAmount, chargeAmountControl);
    } else {
      this.setNonUsdChargeAmount(
        incomingChargeAmount,
        conversionRate,
        chargeAmountControl
      );
    }
  }

  private extractCurrencyCode(incomingChargeCurrency: string): string {
    return incomingChargeCurrency?.includes(' - ')
      ? incomingChargeCurrency.split(' - ')[0].trim()
      : incomingChargeCurrency;
  }

  private setUsdChargeAmount(
    incomingChargeAmount: any,
    chargeAmountControl: AbstractControl | null
  ): void {
    const chargeAmount = Number.parseFloat(String(incomingChargeAmount));
    if (this.isValidNumber(chargeAmount)) {
      chargeAmountControl?.setValue(chargeAmount.toFixed(2), {
        emitEvent: false,
      });
    } else {
      chargeAmountControl?.setValue('', { emitEvent: false });
    }
  }

  private setNonUsdChargeAmount(
    incomingChargeAmount: any,
    conversionRate: string,
    chargeAmountControl: AbstractControl | null
  ): void {
    const chargeAmount =
      Number.parseFloat(String(incomingChargeAmount)) /
      Number.parseFloat(conversionRate);
    if (this.isValidNumber(chargeAmount)) {
      chargeAmountControl?.setValue(chargeAmount.toFixed(2), {
        emitEvent: false,
      });
    } else {
      chargeAmountControl?.setValue('', { emitEvent: false });
    }
  }

  private isValidNumber(value: number): boolean {
    return !Number.isNaN(value) && Number.isFinite(value);
  }

  private patchChargeDescriptionValue(controlName: string, rowIndex: number) {
    const isForAR =
      this.isFrom === selectionTableNames.arChargesForARModule ||
      this.isFrom === selectionTableNames.arChargesForShipmentAndMawb;
    const chargeCodeData = isForAR
      ? this.chargeCodeData
      : this.agentChargeCodeData;
    let descriptionControlName: string;
    if (isForAR) {
      descriptionControlName = 'chargeDescription';
    } else if (this.isFrom === selectionTableNames.ApChargeCode) {
      descriptionControlName = 'description';
    } else if (
      this.isFrom === selectionTableNames.apChargesForShipmentAndMawb
    ) {
      descriptionControlName = 'agentChargeCodeDescription';
    } else {
      descriptionControlName = 'agentChargeDescription';
    }
    const selectedAgentChargeCode = chargeCodeData.find(
      charge =>
        charge.name === this.getRows().at(rowIndex).get(controlName)?.value
    );
    if (selectedAgentChargeCode) {
      this.getRows()
        .at(rowIndex)
        .get(descriptionControlName)
        ?.setValue(selectedAgentChargeCode?.description);
      if (this.isFrom === selectionTableNames.arChargesForARModule) {
        this.getRows()
          .at(rowIndex)
          .get('ibcChargeCodeId')
          ?.setValue(selectedAgentChargeCode?.id);
      } else if (
        this.isFrom === selectionTableNames.arChargesForShipmentAndMawb
      ) {
        this.getRows()
          .at(rowIndex)
          .get('chargeCodeId')
          ?.setValue(selectedAgentChargeCode?.id);
      }
    } else {
      this.getRows().at(rowIndex).get(descriptionControlName)?.setValue('');
      if (this.isFrom === selectionTableNames.arChargesForARModule) {
        this.getRows().at(rowIndex).get('ibcChargeCodeId')?.setValue('');
      } else if (
        this.isFrom === selectionTableNames.arChargesForShipmentAndMawb
      ) {
        this.getRows().at(rowIndex).get('chargeCodeId')?.setValue('');
      }
    }
    this.triggerLinehaulValidation(rowIndex);
  }

  private userDepartmentRowLogic(controlName: string, rowIndex: number) {
    const getFormCurrentIndex = this.getRows().at(rowIndex);
    if (
      !getFormCurrentIndex.get(ApiControls.department)?.value &&
      !getFormCurrentIndex.get(ApiControls.role)?.value &&
      !getFormCurrentIndex.get(ApiControls.location)?.value
    ) {
      this.commonService.setControlRequiredValidation(
        getFormCurrentIndex,
        'department'
      );
      this.commonService.setControlRequiredValidation(
        getFormCurrentIndex,
        'role'
      );
      this.commonService.setControlRequiredValidation(
        getFormCurrentIndex,
        'location'
      );
      this.getDefaultUniqueDropdownValues(rowIndex);
    } else {
      this.filterValueBasedDropdownOption(controlName, rowIndex);
    }
  }

  private filterValueBasedDropdownOption(
    controlName: string,
    rowIndex: number
  ) {
    const getFormCurrentIndex = this.getRows().at(rowIndex);
    if (
      getFormCurrentIndex.get(ApiControls.department)?.value ||
      getFormCurrentIndex.get(ApiControls.role)?.value
    ) {
      this.commonService.removeControlRequiredValidation(
        getFormCurrentIndex,
        'department'
      );
      this.commonService.removeControlRequiredValidation(
        getFormCurrentIndex,
        'role'
      );
    }
    if (controlName === TableControls.department) {
      this.filterDependentDropdownData(
        controlName,
        rowIndex,
        getFormCurrentIndex.get(ApiControls.department)?.value
      );
    } else if (controlName === TableControls.role) {
      this.filterDependentDropdownData(
        controlName,
        rowIndex,
        getFormCurrentIndex.get(ApiControls.role)?.value
      );
    } else if (controlName === TableControls.location) {
      this.inlineLocationHasValue.emit(
        getFormCurrentIndex.get(ApiControls.location)?.value
      );
      this.filterDependentDropdownData(
        controlName,
        rowIndex,
        getFormCurrentIndex.get(ApiControls.location)?.value
      );
    }
  }

  private filterDependentDropdownData(
    controlName: string,
    rowIndex: number,
    selectedId: number
  ) {
    if (!this.departmentsGridData) {
      return;
    }
    const filteredData: any = {
      department: [],
      role: [],
      location: [],
    };
    const selectedDepartmentId = this.getRows()
      .at(rowIndex)
      .get(ApiControls.department)?.value;
    const selectedRoleId = this.getRows().at(rowIndex).get(ApiControls.role)
      ?.value;
    const selectedLocationId = this.getRows()
      .at(rowIndex)
      .get(ApiControls.location)?.value;

    const filterValidConditions = (extraConditions: any) => {
      const validConditions: any = {};
      for (const [key, value] of Object.entries(extraConditions)) {
        if (value !== null && value !== undefined && value !== '') {
          validConditions[key] = value;
        }
      }
      return validConditions;
    };

    const filterData = (
      data: any[],
      filterBy: string,
      filterValue: number,
      extraConditions: any = {}
    ) => {
      const validConditions = filterValidConditions(extraConditions);
      return data.filter(item => {
        let isValid = true;
        if (this.commonService.isValidField(filterValue)) {
          isValid =
            filterBy === 'locationId'
              ? (item.location ?? []).some(
                  (loc: any) => loc.locationId === filterValue
                )
              : item[filterBy] === filterValue;
        }
        for (const [key, value] of Object.entries(validConditions)) {
          if (
            (key !== 'locationId' && item[key] !== value) ||
            (key === 'locationId' &&
              item.location?.every((loc: any) => loc.locationId !== value))
          ) {
            isValid = false;
          }
        }
        return isValid;
      });
    };

    switch (controlName) {
      case TableControls.department:
        filteredData.department = filterData(
          this.departmentsGridData.department,
          'id',
          selectedId,
          {
            roleId: selectedRoleId,
            locationId: selectedLocationId,
          }
        );
        filteredData.role = filterData(
          this.departmentsGridData.role,
          'deptId',
          selectedId,
          {
            roleId: selectedRoleId,
            locationId: selectedLocationId,
          }
        );
        filteredData.location = filterData(
          this.departmentsGridData.location,
          'deptId',
          selectedId,
          {
            roleId: selectedRoleId,
            locationId: selectedLocationId,
          }
        );
        break;

      case TableControls.role:
        filteredData.role = filterData(
          this.departmentsGridData.role,
          'id',
          selectedId,
          {
            deptId: selectedDepartmentId,
            locationId: selectedLocationId,
          }
        );
        filteredData.department = filterData(
          this.departmentsGridData.department,
          'roleId',
          selectedId,
          {
            deptId: selectedDepartmentId,
            locationId: selectedLocationId,
          }
        );
        filteredData.location = filterData(
          this.departmentsGridData.location,
          'roleId',
          selectedId,
          {
            deptId: selectedDepartmentId,
            locationId: selectedLocationId,
          }
        );
        break;

      case TableControls.location:
        filteredData.location = filterData(
          this.departmentsGridData.location,
          'id',
          selectedId,
          {
            deptId: selectedDepartmentId,
            roleId: selectedRoleId,
          }
        );
        filteredData.department = filterData(
          this.departmentsGridData.department.filter((dept: any) =>
            (dept.location ?? []).some(
              (loc: any) => loc.locationId === selectedId
            )
          ),
          'locationId',
          selectedId,
          {
            deptId: selectedDepartmentId,
            roleId: selectedRoleId,
          }
        );
        filteredData.role = filterData(
          this.departmentsGridData.role.filter((role: any) =>
            (role.location ?? []).some(
              (loc: any) => loc.locationId === selectedId
            )
          ),
          'locationId',
          selectedId,
          {
            deptId: selectedDepartmentId,
            roleId: selectedRoleId,
          }
        );
        break;
      default:
        break;
    }

    filteredData.department = this.filterUserDepartmentDropdownEntries(
      filteredData.department
    );
    filteredData.role = this.filterUserDepartmentDropdownEntries(
      filteredData.role
    );

    this.selectedDepartmentsData[rowIndex] = this.getUniqueDropdownDataValues(
      filteredData.department
    );
    this.selectedRolesData[rowIndex] = this.getUniqueDropdownDataValues(
      filteredData.role
    );
    this.selectedLocationsData[rowIndex] = this.getUniqueDropdownDataValues(
      filteredData.location
    );

    this.autocompleteValueChanges(
      rowIndex,
      TableControls.department,
      this.selectedDepartmentsData[rowIndex]
    );
    this.autocompleteValueChanges(
      rowIndex,
      TableControls.role,
      this.selectedRolesData[rowIndex]
    );
    this.autocompleteValueChanges(
      rowIndex,
      TableControls.location,
      this.selectedLocationsData[rowIndex]
    );
  }

  private getDropdownResponseList(control: string) {
    if (this.externalDropdownData?.[control]) {
      return this.externalDropdownData[control];
    }
    const dropdownList = {
      [TableControls.deliveryAgent]: this.deliveryAgentData,
      [TableControls.rateScale]: this.rateScaleData,
      [TableControls.location]: this.locationData,
      [TableControls.group]: this.groupData,
      [TableControls.department]: this.departmentData,
      [TableControls.role]: this.rolesData,
      [TableControls.ibcChargeCode]: this.ibcChargeCodeData,
      [TableControls.agentChargeCode]: this.agentChargeCodeData,
      [TableControls.chargeCode]: this.chargeCodeData,
      [TableControls.agentChargeTypes]: this.agentChargeTypeData,
      [TableControls.apChargeType]: this.apChargeTypeData,
      [TableControls.currency]: this.currencyData,
    };
    return dropdownList[control] || [];
  }

  private getApiControlNames(control: string) {
    if (this.externalApiControlNames?.[control]) {
      return this.externalApiControlNames[control];
    }
    const apiControlName = {
      [TableControls.deliveryAgent]: ApiControls.deliveryAgent,
      [TableControls.rateScale]: ApiControls.rateScale,
      [TableControls.location]: ApiControls.location,
      [TableControls.department]: ApiControls.department,
      [TableControls.role]: ApiControls.role,
      [TableControls.group]: ApiControls.group,
      [TableControls.ibcChargeCode]: ApiControls.ibcChargeCode,
      [TableControls.agentChargeCode]: ApiControls.agentChargeCode,
      [TableControls.agentChargeTypes]: ApiControls.agentChargeTypes,
      [TableControls.apChargeType]: ApiControls.apChargeType,
      [TableControls.outTP]: ApiControls.outTP,
    };
    return apiControlName[control] || '';
  }

  private autocompleteValueChanges(
    formIndex: number,
    controlName: string,
    dropdownData: IDropdownResponse[]
  ) {
    const formControl = this.getRows().at(formIndex).get(controlName);
    if (!formControl) return;

    const controlMap: { [key: string]: Observable<IDropdownResponse[]>[] } = {
      [this.formFields.deliveryAgent]: this.filteredDeliveryAgentDataOptions,
      [this.formFields.rateScale]: this.filteredRateScaleOptions,
      [this.formFields.location]: this.filteredLocationOptions,
      [this.formFields.department]: this.filteredDepartmentOptions,
      [this.formFields.role]: this.filteredRolesOptions,
      [this.formFields.group]: this.filteredGroupOptions,
      [this.formFields.ibcChargeCode]: this.filteredIbcChargeCodeOptions,
      [this.formFields.agentChargeCode]: this.filteredAgentChargeCodeOptions,
      [this.formFields.chargeCode]: this.filteredChargeCodeOptions,
      [this.formFields.agentChargeTypes]: this.filteredAgentChargeTypeOptions,
      [TableControls.apChargeType]: this.filteredApChargeTypeOptions,
      [this.formFields.currency]: this.filteredCurrencyOptions,
    };

    let filteredOptions = controlMap[controlName];

    // Handle Generic/External Autocomplete
    if (
      !filteredOptions &&
      this.externalAutocompleteKeys.includes(controlName)
    ) {
      if (!this.genericFilteredOptions[controlName]) {
        this.genericFilteredOptions[controlName] = [];
      }
      filteredOptions = this.genericFilteredOptions[controlName];
    }

    if (filteredOptions) {
      filteredOptions[formIndex] = this.getFilteredData(
        formControl,
        dropdownData,
        controlName
      );
    }
  }

  private getFilteredData(
    formControl: AbstractControl<any, any> | null,
    dropdownData: any,
    controlName: string
  ): Observable<IDropdownResponse[]> {
    return (
      formControl?.valueChanges.pipe(
        startWith(formControl.value ?? ''),
        map(value => (typeof value === 'string' ? value : value?.name)),
        map(name =>
          name
            ? this.autocompleteFilter(name, dropdownData)
            : dropdownData?.slice() || []
        )
      ) ?? new Observable<IDropdownResponse[]>(observer => observer.next([]))
    );
  }

  private autocompleteFilter(
    name: string,
    dropdownList: IDropdownResponse[]
  ): IDropdownResponse[] {
    const filterValue = name.toLowerCase();
    return dropdownList?.filter(
      opt => opt.name?.toLowerCase().includes(filterValue)
    );
  }

  private checkForCrudTablePageSidebarExpanded() {
    this.commonService.isMenuExpanded
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.setInlineCrudTableWidth =
          this.commonService.setModifiedTooltipWidth(
            this.setInlineCrudTableTooltipEvent,
            this.setInlineCrudTableErrorTooltip,
            this.setInlineCrudTableWidth
          );
      });
  }

  private resolveDepartmentLocationLabel(item: {
    locationName?: string | null;
    facCode?: string | null;
    fac_code?: string | null;
    code?: string | null;
  }): string {
    const candidates = [
      item.locationName,
      item.facCode,
      item.fac_code,
      item.code,
    ];
    return (
      candidates
        .find(
          (value): value is string =>
            typeof value === 'string' && value.trim().length > 0
        )
        ?.trim() ?? ''
    );
  }

  public resolveLocationLabelById(
    locationId: string | number | null | undefined,
    departmentId?: string | number | null | undefined,
    roleId?: string | number | null | undefined
  ): string {
    if (!this.commonService.isValidField(locationId)) {
      return '';
    }

    const exactComboMatch = this.combinationGridData?.find(
      (entry: any) =>
        entry.locationId == locationId &&
        this.isMatchingDepartmentRoleId(entry.deptId, departmentId) &&
        this.isMatchingDepartmentRoleId(entry.roleId, roleId)
    );
    if (exactComboMatch?.locationName?.trim()) {
      return exactComboMatch.locationName.trim();
    }

    const locationMatch = this.locationData?.find(
      (location: IDropdownResponse) => location.id == locationId
    );
    if (locationMatch?.name?.trim()) {
      return locationMatch.name.trim();
    }

    const fallbackCombo = this.combinationGridData?.find(
      (entry: any) => entry.locationId == locationId
    );
    return fallbackCombo?.locationName?.trim() ?? '';
  }

  public refreshUserDepartmentLocationDropdownsForAllRows(): void {
    if (this.isFrom !== selectionTableNames.UserDepartment) {
      return;
    }
    const rowCount = this.getRows()?.length ?? 0;
    for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
      this.getDefaultUniqueDropdownValues(rowIndex);
    }
  }

  private isMatchingDepartmentRoleId(
    entryId: string | number | null | undefined,
    targetId: string | number | null | undefined
  ): boolean {
    if (!this.commonService.isValidField(targetId)) {
      return true;
    }
    return entryId == targetId;
  }

  private getCombinationData() {
    const locationEntries = this.departmentsGridData?.location ?? [];
    return locationEntries.map((item: any) => ({
      deptId: item.deptId ?? '',
      roleId: item.roleId ?? '',
      deptName: item.deptName ?? '',
      locationId: item.locationId ?? '',
      locationName: this.resolveDepartmentLocationLabel(item),
      roleName: item.roleName ?? '',
      departmentRoleId: item.grp_dept_roles_permissions_id ?? '',
    }));
  }

  /** Excludes role-only / department-only rows from the opposite dropdown. */
  private filterUserDepartmentDropdownEntries(items: any[]): any[] {
    if (this.isFrom !== selectionTableNames.UserDepartment) {
      return items ?? [];
    }
    return (items ?? []).filter(item =>
      this.commonService.isValidField(item?.id)
    );
  }

  private getUniqueDropdownDataValues(incomingData: any) {
    return incomingData.reduce((acc: any, curr: any) => {
      if (!acc.some((item: any) => item.id === curr.id)) {
        acc.push(curr);
      }
      return acc;
    }, []);
  }

  public getDefaultUniqueDropdownValues(rowIndex: number) {
    if (this.isFrom === selectionTableNames.UserDepartment) {
      if (!this.departmentData || !this.rolesData || !this.locationData) {
        return;
      }
      this.selectedDepartmentsData[rowIndex] = this.getUniqueDropdownDataValues(
        this.departmentData
      );
      this.autocompleteValueChanges(
        rowIndex,
        TableControls.department,
        this.selectedDepartmentsData[rowIndex]
      );
      this.selectedRolesData[rowIndex] = this.getUniqueDropdownDataValues(
        this.rolesData
      );
      this.autocompleteValueChanges(
        rowIndex,
        TableControls.role,
        this.selectedRolesData[rowIndex]
      );
      this.selectedLocationsData[rowIndex] = this.getUniqueDropdownDataValues(
        this.locationData
      );
      this.autocompleteValueChanges(
        rowIndex,
        TableControls.location,
        this.selectedLocationsData[rowIndex]
      );
    } else {
      this.selectedIbcChargeCodeData[rowIndex] =
        this.getUniqueDropdownDataValues(this.ibcChargeCodeData);
      this.autocompleteValueChanges(
        rowIndex,
        TableControls.ibcChargeCode,
        this.selectedIbcChargeCodeData[rowIndex]
      );
      this.selectedAgentChargeCodeData[rowIndex] =
        this.getUniqueDropdownDataValues(this.agentChargeCodeData);
      this.autocompleteValueChanges(
        rowIndex,
        TableControls.agentChargeCode,
        this.selectedAgentChargeCodeData[rowIndex]
      );
      this.selectedChargeCodeData[rowIndex] = this.getUniqueDropdownDataValues(
        this.chargeCodeData
      );
      this.autocompleteValueChanges(
        rowIndex,
        TableControls.chargeCode,
        this.selectedChargeCodeData[rowIndex]
      );
      this.selectedAgentChargeTypeData[rowIndex] =
        this.getUniqueDropdownDataValues(this.agentChargeTypeData);
      this.autocompleteValueChanges(
        rowIndex,
        TableControls.agentChargeTypes,
        this.selectedAgentChargeTypeData[rowIndex]
      );
      this.selectedApChargeTypeData[rowIndex] =
        this.getUniqueDropdownDataValues(this.apChargeTypeData);
      this.autocompleteValueChanges(
        rowIndex,
        TableControls.apChargeType,
        this.selectedApChargeTypeData[rowIndex]
      );
      this.selectedCurrencyData[rowIndex] = this.getUniqueDropdownDataValues(
        this.currencyData
      );
      this.autocompleteValueChanges(
        rowIndex,
        TableControls.currency,
        this.selectedCurrencyData[rowIndex]
      );
    }
  }

  public validateInput(rowIndex: number, controlName: string): void {
    const control = this.getRows().at(rowIndex).get(controlName);
    if (!control) return;
    const val = (control.value ?? '').toString();
    const isInvalidZero = inValidDecimalInputs.includes(val);
    const errors = control.errors ?? {};
    if (isInvalidZero) {
      control.setErrors({
        ...errors,
        [conditionCheckConstants.invalidDecimal]: true,
      });
    } else if (errors[conditionCheckConstants.invalidDecimal]) {
      const { [conditionCheckConstants.invalidDecimal]: _, ...remaining } =
        errors;
      control.setErrors(Object.keys(remaining).length ? remaining : null);
    }
    this.onBlurInlineCrudTableControls(rowIndex);
  }

  private getAgentChargeCodeListForRow(rowIndex: number): IDropdownResponse[] {
    const row = this.getRows().at(rowIndex);
    const agentId = row?.get('agent')?.value;
    const list = this.originalAgentChargeCodeData || [];
    if (agentId == null || agentId === '') {
      return list;
    }
    return list.filter(
      (item: IDropdownResponse) => Number(item.agentId) === Number(agentId)
    );
  }

  public setAgentChargeCodeOptionsForRow(rowIndex: number): void {
    if (
      this.isFrom !== selectionTableNames.apChargesForShipmentAndMawb ||
      !this.originalAgentChargeCodeData?.length
    ) {
      return;
    }
    const row = this.getRows().at(rowIndex);
    const agentControl = row?.get('agent');
    const chargeCodeControl = row?.get('agentChargeCode');
    if (!agentControl || !chargeCodeControl) return;

    const baseList$ = agentControl.valueChanges.pipe(
      startWith(agentControl.value),
      map((agentId: number | string | null) => {
        const list = this.originalAgentChargeCodeData || [];
        if (agentId == null || agentId === '') return list;
        return list.filter(
          (item: IDropdownResponse) => Number(item.agentId) === Number(agentId)
        );
      })
    );
    const search$ = chargeCodeControl.valueChanges.pipe(
      startWith(chargeCodeControl.value),
      map((val: string | IDropdownResponse) =>
        typeof val === 'string' ? val : val?.name ?? ''
      )
    );
    this.filteredAgentChargeCodeOptions[rowIndex] = combineLatest([
      baseList$,
      search$,
    ]).pipe(
      map(([list, name]) =>
        name ? this.autocompleteFilter(name, list) : list?.slice() || []
      )
    );
  }

  public getAgentChargeCodeDropdownList(): IDropdownResponse[] {
    return this.agentChargeCodeData || [];
  }

  public closeAgentChargeCodeDropdown(select: any) {
    select.close();
  }

  public onAgentChargeCodeAddNewClick(rowIndex: number) {
    this.agentChargeCodeCrudAction.emit({ action: ActionIcons.add, rowIndex });
  }

  public onAgentChargeCodeViewClick(id: number, rowIndex: number) {
    const selectedOption = this.agentChargeCodeData.find(
      (option: IDropdownResponse) => option.id === id
    );
    this.agentChargeCodeCrudAction.emit({
      action: ActionIcons.view,
      id,
      rowIndex,
      data: selectedOption,
    });
  }

  public onAgentChargeCodeSelectionChange(event: any, rowIndex: number) {
    if (event.value) {
      const selectedOption = this.agentChargeCodeData.find(
        (option: IDropdownResponse) => option.name === event.value
      );
      if (selectedOption) {
        this.getRows()
          .at(rowIndex)
          .get(apChargeCodeFields.agentChargeCodeId)
          ?.setValue(selectedOption.id);
        this.patchChargeDescriptionValue(
          apChargeCodeFields.agentChargeCode,
          rowIndex
        );
      }
    }
  }

  private filterAgentChargeDropdown() {
    this.agentChargeCodeData = this.originalAgentChargeCodeData.filter(
      item => item.agentId === this.selectedAgentIdForAgentChargeCode
    );
    this.autocompleteValueChanges(
      0,
      TableControls.agentChargeCode,
      this.agentChargeCodeData
    );
    if (this.isFrom === selectionTableNames.apChargesForApModule) {
      this.toggleAgentChargeCodeBasedOnData();
    }
  }

  private toggleAgentChargeCodeBasedOnData(): void {
    const rows = this.getRows();
    if (!rows?.length) {
      return;
    }

    const isViewMode = this.pageMode === ModeIcons.view;
    const hasNoChargeCodesForAgent =
      this.selectedAgentIdForAgentChargeCode &&
      this.agentChargeCodeData?.length === 0;
    let warnedNoChargeCodes = false;

    rows.controls.forEach(rowControl => {
      const agentChargeCodeControl = rowControl.get('agentChargeCode');
      if (!agentChargeCodeControl) {
        return;
      }
      const isRowLocked = rowControl.get('isRowDisable')?.value === true;
      if (isViewMode || isRowLocked || hasNoChargeCodesForAgent) {
        if (agentChargeCodeControl.enabled) {
          agentChargeCodeControl.disable({ emitEvent: false });
        }
        if (hasNoChargeCodesForAgent && !isViewMode && !warnedNoChargeCodes) {
          warnedNoChargeCodes = true;
          this.snakbarService.openCustomisedSnackBar(
            ModalPopupMessages.noAgentChargeCodesForSelectedAgent,
            ModalPopupMessages.Warning
          );
        }
      } else if (agentChargeCodeControl.disabled) {
        agentChargeCodeControl.enable({ emitEvent: false });
      }
    });
  }

  private isLinehaulEligible(rowIndex: number): boolean {
    const row = this.getRows().at(rowIndex);
    return (
      row.get('shipmentIdentifier')?.value === hyperlinkLabels.mawb &&
      this.commonService.isValidField(
        row.get('shipmentIdentifierValue')?.value
      ) &&
      this.commonService.isValidField(row.get('chargeAmount')?.value) &&
      row.get('agentChargeDescription')?.value ===
        SingleDeleteHeaderText.linehaulCharge
    );
  }

  private buildLinehaulPayload(rowIndex: number) {
    const row = this.getRows().at(rowIndex);
    const chargeAmountRaw = row.get('chargeAmount')?.value;
    const chargeAmount = this.commonService.isValidField(chargeAmountRaw)
      ? Number(chargeAmountRaw)
      : null;
    return {
      shipmentIdentifier: SingleDeleteHeaderText.MawbCaps,
      shipmentIdentifierValue: row.get('shipmentIdentifierValue')?.value,
      chargeAmount: chargeAmount,
      ibcChargeDescription: SingleDeleteHeaderText.linehaulCharge,
    };
  }

  private validateLinehaulCharge(rowIndex: number): void {
    const row = this.getRows().at(rowIndex);
    if (!this.isLinehaulEligible(rowIndex)) {
      row.get('isLinehaulMismatch')?.setValue(false, { emitEvent: false });
      this.updateLinehaulBanner();
      return;
    }
    const identifier = row.get('shipmentIdentifier')?.value;
    const chargeDesc = row.get('agentChargeDescription')?.value;
    if (identifier !== 'MAWB' || chargeDesc !== 'Linehaul Charge') {
      row.get('isLinehaulMismatch')?.setValue(false, { emitEvent: false });
      this.updateLinehaulBanner();
      return;
    }
    if (!row.get('isInputRow')?.value && !row.dirty) {
      return;
    }
    const payload = {
      data: [this.buildLinehaulPayload(rowIndex)],
    };

    this.commonApiService
      .validateAPMawbLineHaul(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          const responseRow = res?.data?.find(
            (d: any) =>
              d.shipmentIdentifierValue ===
              payload.data[0].shipmentIdentifierValue
          );
          const isMismatch = responseRow.isLinehaulChargeMatch === false;
          row.get('isLinehaulMismatch')?.setValue(isMismatch, {
            emitEvent: false,
          });
          this.cdRef.detectChanges();
        },
        error: () => {
          row.get('isLinehaulMismatch')?.setValue(false, { emitEvent: false });
        },
      });
  }

  public updateLinehaulBanner(): void {
    this.showLinehaulMismatchBanner = this.getRows().controls.some(
      control =>
        control.get('shipmentIdentifier')?.value ===
          SingleDeleteHeaderText.MawbCaps &&
        control.get('agentChargeDescription')?.value ===
          SingleDeleteHeaderText.linehaulCharge &&
        control.get('isLinehaulMismatch')?.value === true
    );
  }

  private triggerLinehaulValidation(rowIndex: number): void {
    queueMicrotask(() => {
      this.validateLinehaulCharge(rowIndex);
    });
  }

  public isLinehaulMismatchRow(row: any): boolean {
    return (
      row?.shipmentIdentifier === SingleDeleteHeaderText.MawbCaps &&
      row?.agentChargeDescription === SingleDeleteHeaderText.linehaulCharge &&
      row?.isLinehaulMismatch === true
    );
  }

  public editTransitPoint(rowIndex: number) {
    this.editingRowIndex = rowIndex;
    const row = this.getRows().at(rowIndex);
    row.get('outTP')?.enable();
    this.cdRef.detectChanges();
  }

  public saveTransitPoint(rowIndex: number) {
    const row = this.getRows().at(rowIndex);
    row.get('outTP')?.disable();
    this.editingRowIndex = null;
    this.updatedTransitPointValue.emit(row.getRawValue());
    this.cdRef.detectChanges();
  }

  public closeTransitPoint(rowIndex: number) {
    const row = this.getRows().at(rowIndex);
    row.get('outTP')?.setValue(null);
    row.get('outTP')?.disable();
    this.editingRowIndex = null;
    this.cdRef.detectChanges();
  }

  public focusField(rowIndex: number, fieldName: string): void {
    const row = this.getRows().at(rowIndex);
    const element = this.inlineCrudTableDataSource.data[rowIndex];
    const isPlainText = this.isPlainTextIdentifierDisplay(
      fieldName,
      element,
      rowIndex
    );
    const isHyperlink = this.isShipmentIdentifierValueHyperlink(
      fieldName,
      element,
      rowIndex
    );
    const isRowDisable = row?.get('isRowDisable')?.value;

    if (
      fieldName === 'shipmentIdentifierValue' &&
      (isPlainText || isHyperlink)
    ) {
      if (row && isRowDisable === true && !this.isLockedRow(element)) {
        row.get('isRowDisable')?.setValue(false);
        row.enable();
        const fieldControl = row.get(fieldName);
        if (fieldControl) {
          fieldControl.enable();
        }
        this.getTableData();
      }
    }
    this.cdRef.detectChanges();

    requestAnimationFrame(() => {
      setTimeout(() => {
        try {
          const componentElement = this.elementRef.nativeElement;

          const tableRows = componentElement.querySelectorAll('mat-row');

          if (tableRows.length <= rowIndex) {
            console.warn(
              `Row index ${rowIndex} is out of bounds. Total rows: ${tableRows.length}`
            );
            return;
          }

          const targetRow = tableRows[rowIndex] as HTMLElement;

          let input: HTMLInputElement | null = null;

          const selectors = [
            `input[ng-reflect-name="${fieldName}"]`,
            `input[formControlName="${fieldName}"]`,
            `input[name="${fieldName}"]`,
            `input[formcontrolname="${fieldName}"]`,
          ];

          for (const selector of selectors) {
            input = targetRow.querySelector(selector) as HTMLInputElement;
            if (input) {
              break;
            }
          }

          if (!input) {
            this.cdRef.detectChanges();
            setTimeout(() => {
              for (const selector of selectors) {
                const retryInput = componentElement.querySelector(
                  `mat-row:nth-child(${rowIndex + 1}) ${selector}`
                ) as HTMLInputElement;
                if (retryInput) {
                  retryInput.focus();
                  retryInput.select();
                  return;
                }
              }
            }, 200);
            return;
          }

          input.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
          });

          setTimeout(() => {
            if (input) {
              if (
                document.body.contains(input) &&
                input.offsetParent !== null &&
                !input.disabled
              ) {
                input.focus();
                input.select();
              }
            }
          }, 150);
        } catch (error) {
          console.error('Error focusing field:', error);
        }
      }, 100);
    });
  }
}
